//  ---------------------------------------------------------------------------

import { ed25519 } from '@noble/curves/ed25519.js';
import Exchange from './abstract/pacifica.js';
import { ExchangeError, ArgumentsRequired, InvalidOrder, OrderNotFound, BadRequest, InsufficientFunds, PermissionDenied, RateLimitExceeded, ExchangeNotAvailable, RequestTimeout, NotSupported, AuthenticationError } from './base/errors.js';
import { Precise } from './base/Precise.js';
import { TICK_SIZE } from './base/functions/number.js';
import { eddsa } from './base/functions/crypto.js';
import type { Market, TransferEntry, Balances, Int, OrderBook, OHLCV, Str, FundingRateHistory, Order, OrderType, OrderSide, Trade, Strings, Position, OrderRequest, Dict, NullableDict, Num, Bool, int, Transaction, Currency, TradingFeeInterface, LedgerEntry, FundingRates, FundingRate, OpenInterests, Leverage, MarginMode, Tickers, Ticker, FundingHistory, Endpoint, List } from './base/types.js';

//  ---------------------------------------------------------------------------

/**
 * @class pacifica
 * @augments Exchange
 */
export default class pacifica extends Exchange {
    override describe (): any {
        return this.deepExtend (super.describe (), {
            'id': 'pacifica',
            'name': 'Pacifica',
            'countries': [ ],
            'version': 'v1',
            'isSandboxModeEnabled': false, // is testnet api
            'rateLimit': 600, // 100 credits per minute without an API Config Key (300 with a key)
            'certified': false,
            'pro': true,
            'dex': true,
            'has': {
                'CORS': undefined,
                'spot': true,
                'margin': false,
                'swap': true,
                'future': false,
                'option': false,
                'addMargin': false,
                'borrowCrossMargin': false,
                'borrowIsolatedMargin': false,
                'cancelAllOrders': true,
                'cancelAllOrdersAfter': false,
                'cancelOrder': true,
                'cancelOrders': true,
                'cancelOrdersForSymbols': undefined,
                'closeAllPositions': false,
                'closePosition': false,
                'createMarketBuyOrderWithCost': false,
                'createMarketOrderWithCost': false,
                'createMarketSellOrderWithCost': false,
                'createOrder': true,
                'createOrders': true,
                'createOrderWithTakeProfitAndStopLoss': true,
                'createReduceOnlyOrder': true,
                'createStopOrder': true,
                'editOrder': true,
                'editOrders': false,
                'fetchAccounts': false,
                'fetchBalance': true,
                'fetchBorrowInterest': false,
                'fetchBorrowRateHistories': false,
                'fetchBorrowRateHistory': false,
                'fetchCanceledAndClosedOrders': true,
                'fetchCanceledOrders': true,
                'fetchClosedOrders': true,
                'fetchCrossBorrowRate': false,
                'fetchCrossBorrowRates': false,
                'fetchCurrencies': false,
                'fetchDepositAddress': false,
                'fetchDepositAddresses': false,
                'fetchDeposits': false,
                'fetchDepositWithdrawFee': false,
                'fetchDepositWithdrawFees': false,
                'fetchFundingHistory': true,
                'fetchFundingRate': false,
                'fetchFundingRateHistory': true,
                'fetchFundingRates': true,
                'fetchIndexOHLCV': false,
                'fetchIsolatedBorrowRate': false,
                'fetchIsolatedBorrowRates': false,
                'fetchLedger': true,
                'fetchLeverage': true,
                'fetchLeverageTiers': false,
                'fetchLiquidations': false,
                'fetchMarginMode': true,
                'fetchMarketLeverageTiers': false,
                'fetchMarkets': true,
                'fetchMarkOHLCV': false,
                'fetchMyLiquidations': false,
                'fetchMyTrades': true,
                'fetchOHLCV': true,
                'fetchOpenInterest': true,
                'fetchOpenInterestHistory': false,
                'fetchOpenInterests': true,
                'fetchOpenOrders': true,
                'fetchOrder': true,
                'fetchOrderBook': true,
                'fetchOrders': true,
                'fetchOrderTrades': false,
                'fetchPosition': true,
                'fetchPositionMode': false,
                'fetchPositions': true,
                'fetchPositionsRisk': false,
                'fetchPremiumIndexOHLCV': false,
                'fetchStatus': undefined,
                'fetchTicker': 'emulated',
                'fetchTickers': true,
                'fetchTime': undefined,
                'fetchTrades': true,
                'fetchTradingFee': true,
                'fetchTradingFees': false,
                'fetchTransfer': false,
                'fetchTransfers': false,
                'fetchWithdrawal': false,
                'fetchWithdrawals': false,
                'reduceMargin': false,
                'repayCrossMargin': false,
                'repayIsolatedMargin': false,
                'sandbox': true,
                'setLeverage': true,
                'setMarginMode': true,
                'setPositionMode': false,
                'transfer': true,
                'withdraw': true,
            },
            'timeframes': {
                '1m': '1m',
                '3m': '3m',
                '5m': '5m',
                '15m': '15m',
                '30m': '30m',
                '1h': '1h',
                '2h': '2h',
                '4h': '4h',
                '8h': '8h',
                '12h': '12h',
                '1d': '1d',
                '1w': '1w',
                '1M': '1M',
            },
            'hostname': 'pacifica.fi',
            'urls': {
                'logo': 'https://github.com/user-attachments/assets/03ed021f-cdec-43c8-acb4-941f1282f610',
                'api': {
                    'public': 'https://api.{hostname}',
                    'private': 'https://api.{hostname}',
                },
                'test': {
                    'public': 'https://test-api.{hostname}',
                    'private': 'https://test-api.{hostname}',
                },
                'www': 'https://www.pacifica.fi',
                'doc': 'https://docs.pacifica.fi/api-documentation/api/rest-api',
                'fees': 'https://docs.pacifica.fi/trading-on-pacifica/trading-fees',
                'referral': 'https://app.pacifica.fi?referral=ccxt',
            },
            'api': {
                'public': {
                    'get': {
                        // ~12 weight depends on the limit 3 max for api-key, but min without api-key
                        'info': { 'cost': 1 } as Endpoint<Dict>,
                        'info/fees': { 'cost': 1 } as Endpoint<Dict>,
                        'info/prices': { 'cost': 1 } as Endpoint<Dict>,
                        'kline': { 'cost': 12 } as Endpoint<Dict>,
                        'kline/mark': { 'cost': 12 } as Endpoint<Dict>,
                        'book': { 'cost': 1 } as Endpoint<Dict>,
                        'trades': { 'cost': 1 } as Endpoint<Dict>, // Recent
                        'funding_rate/history': { 'cost': 1 } as Endpoint<Dict>,
                        'loan_pool': { 'cost': 1 } as Endpoint<Dict>,
                        'account': { 'cost': 1 } as Endpoint<Dict>,
                        'account/loan': { 'cost': 1 } as Endpoint<Dict>,
                        'account/settings': { 'cost': 1 } as Endpoint<Dict>,
                        'positions': { 'cost': 1 } as Endpoint<Dict>,
                        'trades/history': { 'cost': 12 } as Endpoint<Dict>,
                        'funding/history': { 'cost': 1 } as Endpoint<Dict>,
                        'portfolio': { 'cost': 1 } as Endpoint<Dict>,
                        'account/balance/history': { 'cost': 12 } as Endpoint<Dict>,
                        'account/spot_balance/history': { 'cost': 1 } as Endpoint<Dict>,
                        'account/spot_asset/deposit/history': { 'cost': 1 } as Endpoint<Dict>,
                        'account/spot_asset/withdraw/history': { 'cost': 1 } as Endpoint<Dict>,
                        'account/spot_asset/withdraw/pending': { 'cost': 1 } as Endpoint<Dict>,
                        'orders': { 'cost': 1 } as Endpoint<Dict>,
                        'orders/history': { 'cost': 12 } as Endpoint<Dict>,
                        'orders/history_by_id': { 'cost': 1 } as Endpoint<Dict>,
                        'spot_assets': { 'cost': 1 } as Endpoint<Dict>,
                        'spot_assets/bridge/info': { 'cost': 1 } as Endpoint<Dict>,
                        'spot_assets/bridge/parameters/{symbol}': { 'cost': 1 } as Endpoint<Dict>,
                        'lake/list': { 'cost': 1 } as Endpoint<Dict>,
                        'account/builder_codes/approvals': { 'cost': 1 } as Endpoint<List>,
                    },
                },
                'private': {
                    'post': {
                        'account/leverage': { 'cost': 1 } as Endpoint<Dict>,
                        'account/margin': { 'cost': 1 } as Endpoint<Dict>,
                        'account/withdraw': { 'cost': 1 } as Endpoint<Dict>,
                        'account/settings/auto_lend_disabled': { 'cost': 1 } as Endpoint<Dict>,
                        'account/settings/spot': { 'cost': 1 } as Endpoint<Dict>,
                        'account/spot_asset/withdraw': { 'cost': 1 } as Endpoint<Dict>,
                        'account/subaccount/create': { 'cost': 1 } as Endpoint<Dict>,
                        'account/subaccount/list': { 'cost': 1 } as Endpoint<Dict>,
                        'account/subaccount/transfer': { 'cost': 1 } as Endpoint<Dict>,
                        'account/subaccount/spot_asset/transfer': { 'cost': 1 } as Endpoint<Dict>,
                        'positions/add_isolated_margin': { 'cost': 1 } as Endpoint<Dict>,
                        'orders/create': { 'cost': 1 } as Endpoint<Dict>,
                        'orders/create_market': { 'cost': 1 } as Endpoint<Dict>,
                        'orders/stop/create': { 'cost': 1 } as Endpoint<Dict>,
                        'positions/tpsl': { 'cost': 1 } as Endpoint<Dict>,
                        'orders/cancel': { 'cost': 0.5 } as Endpoint<Dict>,
                        'orders/cancel_all': { 'cost': 0.5 } as Endpoint<Dict>,
                        'orders/stop/cancel': { 'cost': 0.5 } as Endpoint<Dict>,
                        'orders/edit': { 'cost': 1 } as Endpoint<Dict>,
                        'orders/batch': { 'cost': 1 } as Endpoint<Dict>,
                        'account/builder_codes/approve': { 'cost': 1 } as Endpoint<Dict>,
                        'account/builder_codes/revoke': { 'cost': 1 } as Endpoint<Dict>,
                        'agent/bind': { 'cost': 1 } as Endpoint<Dict>,
                        'account/api_keys/create': { 'cost': 1 } as Endpoint<Dict>,
                        'account/api_keys/revoke': { 'cost': 1 } as Endpoint<Dict>,
                        'account/api_keys': { 'cost': 1 } as Endpoint<Dict>,
                        'lake/add_blacklist': { 'cost': 1 } as Endpoint<Dict>,
                        'lake/add_max_leverage': { 'cost': 1 } as Endpoint<Dict>,
                        'lake/add_whitelist': { 'cost': 1 } as Endpoint<Dict>,
                        'lake/claim_manager': { 'cost': 1 } as Endpoint<Dict>,
                        'lake/claim_referral_code': { 'cost': 1 } as Endpoint<Dict>,
                        'lake/create': { 'cost': 1 } as Endpoint<Dict>,
                        'lake/deposit': { 'cost': 1 } as Endpoint<Dict>,
                        'lake/remove_blacklist': { 'cost': 1 } as Endpoint<Dict>,
                        'lake/remove_max_leverage': { 'cost': 1 } as Endpoint<Dict>,
                        'lake/remove_whitelist': { 'cost': 1 } as Endpoint<Dict>,
                        'lake/update_deposit_cap': { 'cost': 1 } as Endpoint<Dict>,
                        'lake/withdraw': { 'cost': 1 } as Endpoint<Dict>,
                    },
                },
            },
            'fees': {
                'swap': {
                    'taker': this.parseNumber ('0.0004'),
                    'maker': this.parseNumber ('0.00015'),
                },
                'spot': {
                    // https://docs.pacifica.fi/trading-on-pacifica/trading-fees
                    // one unified fee schedule for all product types
                    'taker': this.parseNumber ('0.0004'),
                    'maker': this.parseNumber ('0.00015'),
                },
            },
            //
            // Reminder:
            // If you're using an agent wallet, its private key must also be in the privateKey field in requiredCredentials.
            // However, walletAddress must ALWAYS be equal to the main address. For the agent wallet address, there's a field in options: agentAddress
            //
            'requiredCredentials': {
                'apiKey': false,
                'secret': false,
                'walletAddress': false, // agentAddress, apiKey only in options.
                'privateKey': true, // base58 solana private key
            },
            'exceptions': {
                'exact': {
                    '0': ExchangeError, // INTERNAL
                    '1': ExchangeError, // ACCOUNT_NOT_FOUND
                    '2': ExchangeError, // ACCOUNT_ALREADY_EXISTS
                    '3': ExchangeError, // BOOK_NOT_FOUND
                    '4': InvalidOrder, // INVALID_TICK_LEVEL
                    '5': InsufficientFunds, // INSUFFICIENT_BALANCE
                    '6': OrderNotFound, // ORDER_NOT_FOUND
                    '7': InvalidOrder, // ORDER_AMOUNT_TOO_LOW
                    '8': InvalidOrder, // ORDER_AMOUNT_TOO_HIGH
                    '9': InsufficientFunds, // OVER_WITHDRAWAL
                    '10': InvalidOrder, // OPEN_ORDER_LIMIT_REACHED
                    '11': ExchangeError, // INVALID_LEVERAGE
                    '12': ExchangeError, // CANNOT_UPDATE_MARGIN
                    '13': ExchangeError, // POSITION_NOT_FOUND
                    '14': ExchangeError, // DATABASE_ERROR
                    '15': BadRequest, // INVALID_DEPOSIT_NONCE
                    '16': InvalidOrder, // INVALID_STOP_TICK
                    '17': InvalidOrder, // INVALID_STOP_ORDER_SIDE
                    '18': InvalidOrder, // INVALID_STOP_ORDER_AMOUNT
                    '19': InvalidOrder, // INVALID_STOP_ORDER_REDUCE_ONLY
                    '20': InvalidOrder, // INVALID_ORDER_TYPE
                    '21': InvalidOrder, // INVALID_REDUCE_ONLY_ORDER_SIDE
                    '22': InvalidOrder, // INVALID_REDUCE_ONLY_ORDER_AMOUNT
                    '23': InvalidOrder, // NO_POSITION_FOR_REDUCE_ONLY_ORDER
                    '24': ExchangeError, // INVALID_LIQUIDATION_SIDE
                    '25': InvalidOrder, // NO_REASONABLE_PRICE
                    '26': ExchangeError, // CHANNEL_CLOSED
                    '27': ExchangeError, // RESPONSE_DROPPED
                    '28': InvalidOrder, // IMMEDIATE_LIQUIDATION
                    '29': InvalidOrder, // WITHDRAW_AMOUNT_TOO_LOW
                    '30': InvalidOrder, // PRICE_TOO_FAR_FROM_MARK
                    '31': PermissionDenied, // DAILY_WITHDRAW_LIMIT_EXCEEDED
                    '32': PermissionDenied, // WITHDRAWAL_BLOCKED
                    '33': BadRequest, // INVALID_TRANSFER_RELATIONSHIP
                    '34': PermissionDenied, // SUBACCOUNT_WITHDRAWAL_NOT_ALLOWED
                    '35': PermissionDenied, // SUBACCOUNT_CANNOT_CREATE_SUBACCOUNT
                    '36': InvalidOrder, // DUPLICATE_CLIENT_ORDER_ID
                    '37': InvalidOrder, // UNUSED_CLIENT_ORDER_ID
                    '38': PermissionDenied, // TRADING_DISABLED
                    '39': BadRequest, // INVALID_FEE_MODE
                    '40': PermissionDenied, // NOT_MAIN_ACCOUNT
                    '41': InvalidOrder, // OPEN_INTEREST_LIMIT_EXCEEDED
                    '42': ExchangeError, // EXCHANGE_WITHDRAW_LIMIT_REACHED
                    '43': InvalidOrder, // TWAP_DUPLICATE_CLIENT_ORDER_ID
                    '44': InvalidOrder, // TWAP_UNUSED_CLIENT_ORDER_ID
                    '45': InvalidOrder, // TWAP_ORDER_FAIL_TO_GET_SUB_ORDER_AMOUNT
                    '46': InvalidOrder, // TWAP_ORDER_DURATION_TOO_SHORT
                    '47': OrderNotFound, // TWAP_ORDER_NOT_FOUND
                    '48': InvalidOrder, // TWAP_ORDER_COUNT_PER_SYMBOL_LIMIT_EXCEEDED
                    '49': InvalidOrder, // POSITION_TPSL_LIMIT_EXCEEDED
                    '50': BadRequest, // INVALID_BUILDER_CODE
                    '51': NotSupported, // UNSUPPORTED_OPERATION
                    '52': InvalidOrder, // INVALID_TICK_SIZE
                    '53': InvalidOrder, // ORDER_BLOCKED_BY_LOAN_POOL_STRESS
                    '54': ExchangeError, // ASSET_ALREADY_EXISTS
                    '55': ExchangeError, // ASSET_NOT_FOUND
                    '56': ExchangeError, // ASSET_NOT_ACTIVE
                    '59': InvalidOrder, // INVALID_AMOUNT
                    '61': InsufficientFunds, // SPOT_WITHDRAWAL_EXCEEDS_COLLATERAL
                    '62': InsufficientFunds, // INSUFFICIENT_SPOT_BALANCE
                    '63': ExchangeError, // MISSING_MARK_PRICE
                    '64': BadRequest, // INVALID_FLOOR_PRICE_PCT
                    '65': InsufficientFunds, // SPOT_EXCLUSION_BREACHES_COLLATERAL
                    '66': ExchangeError, // LAKE_NOT_FOUND
                    '67': ExchangeError, // LAKE_ADDRESS_COLLISION
                    '68': InvalidOrder, // LAKE_MIN_DEPOSIT_AMOUNT
                    '69': InvalidOrder, // LAKE_INVALID_SHARES
                    '70': InsufficientFunds, // LAKE_OVER_WITHDRAWAL
                    '71': ExchangeError, // LAKE_NICKNAME_ALREADY_EXISTS
                    '72': PermissionDenied, // LAKE_WITHDRAWAL_NOT_ALLOWED
                    '73': PermissionDenied, // LAKE_MANAGER_IS_SUBLAKE
                    '74': PermissionDenied, // LAKE_NOT_CREATOR
                    '75': InvalidOrder, // LAKE_DEPOSIT_CAP_EXCEEDED
                    '76': PermissionDenied, // LAKE_WITHDRAW_TOO_EARLY
                    '77': BadRequest, // LAKE_INVALID_REV_SHARE_CONFIG
                    '78': InsufficientFunds, // LAKE_DEPOSITOR_OVER_WITHDRAWAL
                    '79': ExchangeError, // LAKE_ALREADY_HAS_MANAGER
                    '80': InvalidOrder, // LAKE_MANAGER_BALANCE_PORTION_TOO_LOW
                    '81': BadRequest, // LAKE_INVALID_BALANCE_PORTION_CONFIG
                    '82': InvalidOrder, // LAKE_LIQUIDATION_PORTION_ABOVE_MIN_PORTION
                    '83': ExchangeNotAvailable, // LAKE_TRADING_HALTED
                    '84': BadRequest, // INVALID_WITHDRAW_NONCE
                    '85': BadRequest, // LAKE_INVALID_WITHDRAW_WINDOW_CONFIG
                    '86': BadRequest, // LAKE_WITHDRAW_DURATION_ABOVE_WINDOW
                    '87': PermissionDenied, // LAKE_WITHDRAW_WINDOW_CLOSED
                    '88': BadRequest, // INVALID_SPOT_DEPOSIT_NONCE
                    '89': BadRequest, // SPOT_DEPOSIT_NONCE_GAP
                    '90': BadRequest, // INVALID_SPOT_WITHDRAW_NONCE
                    '91': ExchangeError, // SPOT_BRIDGE_NOT_FOUND
                    '92': ExchangeNotAvailable, // SPOT_BRIDGE_INACTIVE
                    '93': BadRequest, // LAKE_SYMBOL_NOT_ALLOWED
                    '94': InvalidOrder, // LAKE_MAX_LEVERAGE_EXCEEDED
                    '95': ExchangeError, // GAME_CONFIG_NOT_FOUND
                    '96': ExchangeError, // GAME_ACCOUNT_NOT_FOUND
                    '97': ExchangeError, // GAME_ACCOUNT_ADDRESS_COLLISION
                    '99': InvalidOrder, // GAME_DEPOSIT_CAP_EXCEEDED
                    '100': PermissionDenied, // GAME_OPERATION_NOT_ALLOWED
                    '101': ExchangeNotAvailable, // GAME_ALREADY_ENDED
                    '102': BadRequest, // GAME_INVALID_CONFIG
                    '103': PermissionDenied, // GAME_ACCOUNT_WITHDRAWAL_NOT_ALLOWED
                    '104': InvalidOrder, // GAME_LEVERAGE_EXCEEDED
                    '105': InvalidOrder, // GAME_DEPOSIT_BELOW_MINIMUM
                    '106': NotSupported, // REDUCE_ONLY_NOT_SUPPORTED_FOR_SPOT
                    '107': NotSupported, // TP_SL_NOT_SUPPORTED_FOR_SPOT
                    '108': NotSupported, // BUILDER_CODE_NOT_SUPPORTED_FOR_SPOT
                    '109': NotSupported, // MARGIN_SETTINGS_NOT_APPLICABLE_FOR_SPOT
                    '110': BadRequest, // INVALID_BOOK_CONFIG
                    '111': ExchangeNotAvailable, // TAP_GAME_NOT_ACTIVE
                    '112': InvalidOrder, // TAP_GAME_INVALID_AMOUNT
                    '113': ExchangeError, // TAP_GAME_ERROR
                    '114': ExchangeError, // INVALID_COLLATERAL_LIMIT / LAKE_SELF_DEPOSIT_NOT_ALLOWED
                    '115': ExchangeError, // SPOT_COLLATERAL_LIMIT_BREACHES_COLLATERAL / LAKE_DEPOSITOR_NOT_WHITELISTED
                    '116': ExchangeError, // DAILY_SPOT_WITHDRAW_LIMIT_EXCEEDED / RFQ_SELF_QUOTE_NOT_ALLOWED
                    '117': ExchangeError, // EXCHANGE_SPOT_WITHDRAW_LIMIT_REACHED / RFQ_NOT_SUPPORTED_FOR_SPOT
                    '118': ExchangeError, // INVALID_SPOT_LIMIT / RFQ_MISSING_CLIENT_ORDER_ID
                    '119': ExchangeNotAvailable, // ORACLE_NOT_AVAILABLE
                    '120': PermissionDenied, // VAULT_WITHDRAWAL_NOT_ALLOWED
                    '121': InvalidOrder, // RFQ_QUOTE_WORSE_THAN_BOOK
                    '400': BadRequest, // Bad Request; INVALID_REQUEST_CODE
                    '401': AuthenticationError, // INVALID_SIGNATURE_CODE
                    '402': AuthenticationError, // INVALID_SIGNER_CODE
                    '403': PermissionDenied, // Forbidden: restricted region; UNAUTHORIZED_REQUEST_CODE
                    '404': BadRequest, // Not Found
                    '409': ExchangeError, // Conflict
                    '420': ExchangeError, // ENGINE_ERROR_CODE
                    '422': ExchangeError, // Business Logic Error - See below
                    '429': RateLimitExceeded, // Too Many Requests - Rate limit exceeded; RATE_LIMIT_EXCEEDED_CODE
                    '500': ExchangeError, // Internal Server Error; UNKNOWN_ERROR_CODE
                    '503': ExchangeNotAvailable, // Service Unavailable
                    '504': RequestTimeout, // Gateway Timeout
                },
                'broad': {
                    'UNKNOWN': ExchangeError,
                    'ACCOUNT_NOT_FOUND': ExchangeError,
                    'BOOK_NOT_FOUND': ExchangeError,
                    'INVALID_TICK_LEVEL': InvalidOrder,
                    'INSUFFICIENT_BALANCE': InsufficientFunds,
                    'ORDER_NOT_FOUND': OrderNotFound,
                    'OVER_WITHDRAWAL': InsufficientFunds,
                    'INVALID_LEVERAGE': ExchangeError,
                    'CANNOT_UPDATE_MARGIN': ExchangeError,
                    'POSITION_NOT_FOUND': ExchangeError,
                    'POSITION_TPSL_LIMIT_EXCEEDED': InvalidOrder,
                },
            },
            'precisionMode': TICK_SIZE,
            'commonCurrencies': { },
            'options': {
                'agentAddress': undefined,
                'apiKey': undefined,
                'builderCode': 'CCXT', // case sensitive
                'feeRate': '0.01', // default rate for builder fee approval 0.01%
                'builderFee': true,
                'batchOrdersMax': 10,
                'defaultType': 'swap',
                'defaultSlippage': '0.5',
                'expiryWindow': 5000,
                'maxCostHugeWithApiKey': 4,
                'marketHelperProps': [ ],
                'defaultMarginMode': 'cross',
                'builderSupportOperations': {
                    'create_market_order': true,
                    'create_limit_order': true,
                    'create_stop_order': true,
                    'set_position_tpsl': true,
                },
            },
            'features': {
                'default': {
                    'sandbox': true,
                    'createOrder': {
                        'marginMode': false,
                        'triggerPrice': false,
                        'triggerPriceType': undefined,
                        'triggerDirection': false,
                        'stopLossPrice': false,
                        'takeProfitPrice': false,
                        'attachedStopLossTakeProfit': {
                            'triggerPriceType': {
                                'last': false,
                                'mark': false,
                                'index': false,
                            },
                            'triggerPrice': true,
                            'type': true,
                            'price': true,
                        },
                        'timeInForce': {
                            'IOC': true,
                            'FOK': false,
                            'PO': true,
                            'GTD': false,
                        },
                        'hedged': false,
                        'trailing': false,
                        'leverage': false,
                        'marketBuyByCost': false,
                        'marketBuyRequiresPrice': false,
                        'selfTradePrevention': false,
                        'iceberg': false,
                    },
                    'createOrders': {
                        'max': 10,
                    },
                    'editOrder': {
                        'side': false,
                        'type': false,
                    },
                    'fetchMyTrades': {
                        'marginMode': false,
                        'limit': 100,
                        'daysBack': undefined,
                        'untilDays': undefined,
                        'symbolRequired': false,
                    },
                    'fetchOrder': {
                        'marginMode': false,
                        'trigger': false,
                        'trailing': false,
                        'symbolRequired': false,
                    },
                    'fetchOpenOrders': {
                        'marginMode': false,
                        'limit': 100,
                        'trigger': false,
                        'trailing': false,
                        'symbolRequired': false,
                    },
                    'fetchOrders': {
                        'marginMode': false,
                        'limit': 100,
                        'daysBack': undefined,
                        'untilDays': undefined,
                        'trigger': false,
                        'trailing': false,
                        'symbolRequired': false,
                    },
                    'fetchClosedOrders': {
                        'marginMode': false,
                        'limit': 100,
                        'daysBack': undefined,
                        'daysBackCanceled': undefined,
                        'untilDays': undefined,
                        'trigger': false,
                        'trailing': false,
                        'symbolRequired': false,
                    },
                    'fetchOHLCV': {
                        'limit': 3950,
                    },
                    'fetchLedger': {
                        'code': false,
                    },
                },
                'forPerps': {
                    'extends': 'default',
                    'createOrder': {
                        'stopLossPrice': true,
                        'takeProfitPrice': true,
                        'attachedStopLossTakeProfit': undefined,
                    },
                },
                'spot': undefined,
                'swap': {
                    'linear': {
                        'extends': 'forPerps',
                    },
                    'inverse': {
                        'extends': 'forPerps',
                    },
                },
                'future': {
                    'linear': {
                        'extends': 'forPerps',
                    },
                    'inverse': {
                        'extends': 'forPerps',
                    },
                },
            },
        });
    }

    async initializeClient () {
        try {
            await this.handleBuilderFeeApproval ();
        } catch (e) {
            return false;
        }
        return true;
    }

    async handleBuilderFeeApproval () {
        if (this.isSandboxModeEnabled) { // At this stage, building codes are mostly only on the mainnet.
            return false;
        }
        const buildFee = this.safeBool (this.options, 'builderFee', true);
        if (!buildFee) {
            return false; // skip if builder fee is not enabled
        }
        const approvedBuilderFee = this.safeBool (this.options, 'approvedBuilderFee', false);
        if (approvedBuilderFee) {
            return true; // skip if builder fee is already approved
        }
        try {
            const builder = this.safeString (this.options, 'builderCode', 'CCXT'); // case sensitive
            const maxFeeRate = this.safeString (this.options, 'feeRate', '0.01');
            await this.approveBuilderCode (builder, maxFeeRate);
            this.options['approvedBuilderFee'] = true;
        } catch (e) {
            this.options['builderFee'] = false; // disable builder fee if an error occurs
        }
        return true;
    }

    /**
     * @method
     * @name pacifica#fetchMarkets
     * @description retrieves data on all markets for pacifica
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/markets/get-market-info
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} an array of [market structures](https://docs.ccxt.com/#/?id=market-structure)
     */
    override async fetchMarkets (params = {}): Promise<Market[]> {
        const response = await this.publicGetInfo (params); // meta
        // {
        //   "success": true,
        //   "data": [
        //     {
        //       "symbol": "BTC",
        //       "tick_size": "1",
        //       "min_tick": "0",
        //       "max_tick": "1000000",
        //       "lot_size": "0.00001",
        //       "max_leverage": 50,
        //       "isolated_only": false,
        //       "min_order_size": "10",
        //       "max_order_size": "5000000",
        //       "funding_rate": "0.0000125",
        //       "next_funding_rate": "0.0000125",
        //       "created_at": 1748881333944,
        //       "instrument_type": "perpetual",
        //       "base_asset": "BTC"
        //     },
        //     {
        //       "symbol": "SOL-USDC",
        //       "tick_size": "0.01",
        //       "min_tick": "0",
        //       "max_tick": "1000000",
        //       "lot_size": "0.001",
        //       "max_leverage": 1,
        //       "isolated_only": false,
        //       "min_order_size": "10",
        //       "max_order_size": "1000000",
        //       "funding_rate": "0",
        //       "next_funding_rate": "0",
        //       "created_at": 1776615970246,
        //       "instrument_type": "spot",
        //       "base_asset": "SOL"
        //     },
        //   ],
        //   "error": null,
        //   "code": null
        // }
        const markets = this.safeList (response, 'data', []);
        return this.parseMarkets (markets);
    }

    /**
     * @method
     * @name pacifica#fetchSwapMarkets
     * @description retrieves data on all swap markets for pacifica
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/markets/get-market-info
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} an array of objects representing market data
     */
    async fetchSwapMarkets (params: any = {}): Promise<Market[]> {
        const markets = await this.fetchMarkets (params);
        return this.filterBy (markets, 'type', 'swap') as Market[];
    }

    override parseMarket (market: Dict): Market {
        //     {
        //       "symbol": "BTC",
        //       "tick_size": "1",
        //       "min_tick": "0",
        //       "max_tick": "1000000",
        //       "lot_size": "0.00001",
        //       "max_leverage": 50,
        //       "isolated_only": false,
        //       "min_order_size": "10",
        //       "max_order_size": "5000000",
        //       "funding_rate": "0.0000125",
        //       "next_funding_rate": "0.0000125",
        //       "created_at": 1748881333944,
        //       "instrument_type": "perpetual",
        //       "base_asset": "BTC"
        //     },
        //     {
        //       "symbol": "SOL-USDC",
        //       "tick_size": "0.01",
        //       "min_tick": "0",
        //       "max_tick": "1000000",
        //       "lot_size": "0.001",
        //       "max_leverage": 1,
        //       "isolated_only": false,
        //       "min_order_size": "10",
        //       "max_order_size": "1000000",
        //       "funding_rate": "0",
        //       "next_funding_rate": "0",
        //       "created_at": 1776615970246,
        //       "instrument_type": "spot",
        //       "base_asset": "SOL"
        //     },
        const id = this.safeString (market, 'symbol');
        const baseId = this.safeString (market, 'base_asset', id);
        const instrumentType = this.safeString (market, 'instrument_type');
        const isSpot = (instrumentType === 'spot');
        const isSwap = !isSpot;
        let quoteId = 'USDC';
        let settleId: Str = undefined;
        let type = 'spot';
        let linear: Bool = undefined;
        let inverse: Bool = undefined;
        let contractSize: Num = undefined;
        let minLeverage: Num = undefined;
        let maxLeverage: Int = undefined;
        let crossMargin: Bool = undefined;
        let isolatedMargin: Bool = undefined;
        if (id === undefined) {
            throw new ExchangeError (this.id + ' parseMarket() missing id');
        }
        if (isSpot) {
            const idParts = id.split ('-');
            quoteId = this.safeString (idParts, 1, quoteId);
        }
        const isolatedOnly = this.safeBool (market, 'isolated_only', false);
        if (isSwap) {
            settleId = quoteId;
            type = 'swap';
            linear = true;
            inverse = false;
            contractSize = this.parseNumber ('1');
            minLeverage = 1;
            maxLeverage = this.safeInteger (market, 'max_leverage');
            crossMargin = !isolatedOnly;
            isolatedMargin = true;
        }
        const base = this.safeCurrencyCode (baseId);
        const quote = this.safeCurrencyCode (quoteId);
        const settle = this.safeCurrencyCode (settleId);
        let symbol = base + '/' + quote;
        if (isSwap) {
            symbol = symbol + ':' + settle;
        }
        const fees = this.safeDict (this.fees, type, {});
        const taker = this.safeNumber (fees, 'taker');
        const maker = this.safeNumber (fees, 'maker');
        const amountPrecision = this.safeNumber (market, 'lot_size');
        const pricePrecision = this.safeNumber (market, 'tick_size');
        const active = true; // there is no non-active markets comes from endpoint market info
        return this.safeMarketStructure ({
            'id': id,
            'symbol': symbol,
            'base': base,
            'quote': quote,
            'settle': settle,
            'baseId': baseId,
            'quoteId': quoteId,
            'settleId': settleId,
            'type': type,
            'spot': isSpot,
            'margin': false,
            'swap': isSwap,
            'future': false,
            'option': false,
            'active': active,
            'contract': isSwap,
            'linear': linear,
            'inverse': inverse,
            'taker': taker,
            'maker': maker,
            'contractSize': contractSize,
            'expiry': undefined,
            'expiryDatetime': undefined,
            'strike': undefined,
            'optionType': undefined,
            'precision': {
                'amount': amountPrecision,
                'price': pricePrecision,
            },
            'limits': {
                'leverage': {
                    'min': minLeverage,
                    'max': maxLeverage,
                },
                'amount': {
                    'min': undefined,
                    'max': undefined,
                },
                'price': {
                    'min': this.safeNumber (market, 'min_tick'),
                    'max': this.safeNumber (market, 'max_tick'),
                },
                'cost': {
                    'min': this.safeNumber (market, 'min_order_size'),
                    'max': this.safeNumber (market, 'max_order_size'),
                },
            },
            'created': this.safeInteger (market, 'created_at'),
            'marginModes': {
                'cross': crossMargin,
                'isolated': isolatedMargin,
            },
            'info': market,
        });
    }

    /**
     * @method
     * @name pacifica#fetchBalance
     * @description query for balance and get the amount of funds available for trading or funds locked in orders
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/account/get-account-info
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @returns {object} a [balance structure]{@link https://docs.ccxt.com/?id=balance-structure}
     */
    override async fetchBalance (params = {}): Promise<Balances> {
        let userAccount: Str = undefined;
        [ userAccount, params ] = this.handleOriginAndSingleAddress ('fetchBalance', params);
        const request = {
            'account': userAccount,
        };
        const response = await this.publicGetAccount (this.extend (request, params));
        // {
        //   "success": true,
        //   "data": {
        //     "balance": "2000.000000",
        //     "fee_level": 0,
        //     "maker_fee": "0.00015",
        //     "taker_fee": "0.0004",
        //     "account_equity": "2150.250000",
        //     "available_to_spend": "1800.750000",
        //     "available_to_withdraw": "1500.850000",
        //     "pending_balance": "0.000000",
        //     "total_margin_used": "349.500000",
        //     "cross_mmr": "420.690000",
        //     "positions_count": 2,
        //     "orders_count": 3,
        //     "stop_orders_count": 1,
        //     "updated_at": 1716200000000,
        //     "use_ltp_for_stop_orders": false
        //   },
        //   "error": null,
        //   "code": null
        // }
        const data = this.safeDict (response, 'data', {});
        const result: Dict = {
            'info': data,
        };
        result['free'] = {};
        result['used'] = {};
        result['total'] = {};
        const totalBalance = this.safeNumber (data, 'account_equity');
        const usedMargin = this.safeNumber (data, 'total_margin_used');
        const freeBalance = this.safeNumber (data, 'available_to_spend');
        result['total']['USDC'] = totalBalance;
        result['used']['USDC'] = usedMargin;
        result['free']['USDC'] = freeBalance;
        const timestamp = this.safeInteger (data, 'updated_at');
        result['timestamp'] = timestamp;
        result['datetime'] = this.iso8601 (timestamp);
        return this.safeBalance (result);
    }

    /**
     * @method
     * @name pacifica#fetchLeverage
     * @description fetch the set leverage for a market
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/account/get-account-settings
     * @param {string} symbol  unified symbol of the market
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @returns {object} a [leverage structure]{@link https://docs.ccxt.com/?id=leverage-structure}
     */
    override async fetchLeverage (symbol: string, params = {}): Promise<Leverage> {
        await this.loadAccountSettings ();
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        let userAccount: Str = undefined;
        [ userAccount, params ] = this.handleOriginAndSingleAddress ('fetchLeverage', params);
        const cacheAddress = this.walletAddress;
        let settings: NullableDict = undefined;
        if (userAccount === cacheAddress) {
            settings = this.handleOption ('fetchLeverage', 'settings');
        } else {
            const request: Dict = {
                'account': userAccount,
            };
            settings = await this.fetchAccountSettings (this.extend (request, params));
        }
        const setting = this.safeDict (settings, symbol);
        if (setting === undefined) {
            // NOTE: Upon account creation, all markets have margin settings default to cross margin and leverage default to max.
            // When querying this endpoint, all markets with default margin and leverage settings on this account will return blank.
            return this.parseLeverageFromMarket (market);
        } else {
            return this.parseLeverageFromSetting (symbol, setting);
        }
    }

    parseLeverageFromSetting (symbol: Str, setting: Dict): Leverage {
        // {
        //   "WLFI/USDC:USDC": {
        //       "symbol": "WLFI",
        //       "isolated": false,
        //       "leverage": 5,
        //       "created_at": 1758085929703,
        //       "updated_at": 1758086074002
        //    },
        // }
        const isIsolated = this.safeBool (setting, 'isolated', false);
        const leverage = this.safeInteger (setting, 'leverage');
        const marginMode = isIsolated ? 'isolated' : 'cross';
        return {
            'info': setting,
            'symbol': symbol,
            'marginMode': marginMode,
            'longLeverage': leverage,
            'shortLeverage': leverage,
        } as Leverage;
    }

    parseLeverageFromMarket (market: Market): Leverage {
        const marketLimits = this.safeDict (market, 'limits', {});
        const leverageLimits = this.safeDict (marketLimits, 'leverage', {});
        return {
            'info': market,
            'symbol': this.safeString (market, 'symbol'),
            'marginMode': this.handleOption ('fetchLeverage', 'defaultMarginMode', 'cross'),
            'longLeverage': this.safeInteger (leverageLimits, 'max'),
            'shortLeverage': this.safeInteger (leverageLimits, 'max'),
        } as Leverage;
    }

    /**
     * @method
     * @name pacifica#fetchAccountSettings
     * @description fetch account's market settings. Settings are cached for walletAddress. To refresh the cache, call loadAccountSettings with refresh=true
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/account/get-account-settings
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @returns {object} Dict repacked from list by symbol key
     */
    async fetchAccountSettings (params = {}): Promise<Dict> {
        let userAccount: Str = undefined;
        [ userAccount, params ] = this.handleOriginAndSingleAddress ('fetchAccountSettings', params);
        const request: Dict = {
            'account': userAccount,
        };
        const response = await this.publicGetAccountSettings (this.extend (request, params));
        // {
        //   "success": true,
        //   "data": [
        //     {
        //       "symbol": "WLFI",
        //       "isolated": false,
        //       "leverage": 5,
        //       "created_at": 1758085929703,
        //       "updated_at": 1758086074002
        //     }
        //   ],
        //   "error": null,
        //   "code": null
        // }
        return this.parseAccountSettings (this.safeList (response, 'data', []));
    }

    async loadAccountSettings (refresh: boolean = false, params = {}) {
        let settings = this.handleOption ('loadAccountSettings', 'settings');
        if ((settings === undefined) || (refresh === true)) {
            this.options['settings'] = this.createSafeDictionary ();
            settings = await this.fetchAccountSettings (params);
            this.options['settings'] = settings;
        }
    }

    parseAccountSettings (settings: any[]): Dict {
        const settingsLen = settings.length;
        if (settingsLen === 0) {
            return {};
        }
        const settingsBySymbol: Dict = {};
        for (let i = 0; i < settings.length; i++) {
            const marketId = settings[i]['symbol'];
            const market = this.safeMarket (marketId);
            const symbol = market['symbol'];
            settingsBySymbol[symbol] = settings[i];
        }
        return settingsBySymbol;
    }

    /**
     * @method
     * @name pacifica#fetchMarginMode
     * @description fetches the margin mode of the trading pair
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/account/get-account-settings
     * @param {string} symbol unified symbol of the market to fetch the margin mode for
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @returns {object} a [margin mode structure]{@link https://docs.ccxt.com/?id=margin-mode-structure}
     */
    override async fetchMarginMode (symbol: string, params = {}): Promise<MarginMode> {
        await this.loadAccountSettings ();
        let userAccount: Str = undefined;
        [ userAccount, params ] = this.handleOriginAndSingleAddress ('fetchMarginMode', params);
        const cacheAddress = this.walletAddress;
        let settings: NullableDict = undefined;
        if (userAccount === cacheAddress) {
            settings = this.handleOption ('fetchMarginMode', 'settings');
        } else {
            const request: Dict = {
                'account': userAccount,
            };
            settings = await this.fetchAccountSettings (this.extend (request, params));
        }
        // {
        //   "WLFI/USDC:USDC": {
        //       "symbol": "WLFI",
        //       "isolated": false,
        //       "leverage": 5,
        //       "created_at": 1758085929703,
        //       "updated_at": 1758086074002
        //    },
        // }
        const setting = this.safeDict (settings, symbol);
        if (setting === undefined) {
            // NOTE: Upon account creation, all markets have margin settings default to cross margin and leverage default to max.
            // When querying this endpoint, all markets with default margin and leverage settings on this account will return blank.
            return {
                'symbol': symbol,
                'marginMode': this.handleOption ('fetchMarginMode', 'defaultMarginMode', 'cross'),
            } as MarginMode;
        } else {
            return this.parseMarginModeFromSetting (symbol, setting);
        }
    }

    parseMarginModeFromSetting (symbol: Str, setting: Dict): MarginMode {
        // {
        //       "symbol": "WLFI",
        //       "isolated": false,
        //       "leverage": 5,
        //       "created_at": 1758085929703,
        //       "updated_at": 1758086074002
        //
        // }
        const isIsolated = this.safeBool (setting, 'isolated', false);
        const marginMode = isIsolated ? 'isolated' : 'cross';
        return {
            'symbol': symbol,
            'marginMode': marginMode,
            'info': setting,
        } as MarginMode;
    }

    /**
     * @method
     * @name pacifica#fetchOrderBook
     * @description fetches information on open orders with bid (buy) and ask (sell) prices, volumes and other data
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/markets/get-orderbook
     * @param {string} symbol unified symbol of the market to fetch the order book for
     * @param {int} [limit] the maximum amount of order book entries to return
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.aggLevel] aggregation level for price grouping. Defaults to 1. Can be 1, 10, 100, 1000, 10000
     * @returns {object} an [order book structure]{@link https://docs.ccxt.com/?id=order-book-structure}
     */
    override async fetchOrderBook (symbol: string, limit: Int = undefined, params = {}): Promise<OrderBook> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        let aggLevel: Int = undefined;
        [ aggLevel, params ] = this.handleOptionAndParams (params, 'fetchOrderBook', 'aggLevel', 1);
        const request: Dict = {
            'symbol': market['id'],
            'agg_level': aggLevel,
        };
        const response = await this.publicGetBook (this.extend (request, params));
        // {
        //   "success": true,
        //   "data": {
        //     "s": "BTC",
        //     "l": [
        //       [
        //         {
        //           "p": "106504",
        //           "a": "0.26203",
        //           "n": 1
        //         },
        //         {
        //           "p": "106498",
        //           "a": "0.29281",
        //           "n": 1
        //         }
        //       ],
        //       [
        //         {
        //           "p": "106559",
        //           "a": "0.26802",
        //           "n": 1
        //         },
        //         {
        //           "p": "106564",
        //           "a": "0.3002",
        //           "n": 1
        //         },
        //       ]
        //     ],
        //     "t": 1751370536325
        //   },
        //   "error": null,
        //   "code": null
        // }
        const data = this.safeDict (response, 'data', {});
        const levels = this.safeList (data, 'l', []);
        const result: Dict = {
            'bids': this.safeList (levels, 0, []),
            'asks': this.safeList (levels, 1, []),
        };
        const timestamp = this.safeInteger (data, 't');
        return this.parseOrderBook (result, this.safeSymbol (undefined, market), timestamp, 'bids', 'asks', 'p', 'a');
    }

    /**
     * @method
     * @name pacifica#fetchFundingRates
     * @description retrieves data on all swap markets for pacifica
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/markets/get-prices
     * @param {string[]} [symbols] list of unified market symbols
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} an array of objects representing market data
     */
    override async fetchFundingRates (symbols: Strings = undefined, params = {}): Promise<FundingRates> {
        const response = await this.publicGetInfoPrices (params);
        //
        //  {
        //     "success": true,
        //     "data": [
        //         {
        //         "funding": "0.00010529",
        //         "mark": "1.084819",
        //         "mid": "1.08615",
        //         "next_funding": "0.00011096",
        //         "open_interest": "3634796",
        //         "oracle": "1.084524",
        //         "symbol": "XPL",
        //         "timestamp": 1759222967974,
        //         "volume_24h": "20896698.0672",
        //         "yesterday_price": "1.3412"
        //         }
        //     ],
        //     "error": null,
        //     "code": null
        //   }
        //
        const result = this.safeList (response, 'data', []);
        return this.parseFundingRates (result, symbols);
    }

    override parseFundingRate (info: any, market: Market = undefined): FundingRate {
        //
        //      {
        //         "funding": "0.00010529",
        //         "mark": "1.084819",
        //         "mid": "1.08615",
        //         "next_funding": "0.00011096",
        //         "open_interest": "3634796",
        //         "oracle": "1.084524",
        //         "symbol": "XPL",
        //         "timestamp": 1759222967974,
        //         "volume_24h": "20896698.0672",
        //         "yesterday_price": "1.3412"
        //       }
        //
        const marketId = this.safeString (info, 'symbol');
        market = this.safeMarket (marketId, market);
        const symbol = market['symbol'];
        const funding = this.safeNumber (info, 'funding');
        const markPx = this.safeNumber (info, 'mark');
        const oraclePx = this.safeNumber (info, 'oracle');
        const nextFundingRate = this.safeNumber (info, 'next_funding');
        const timestamp = this.safeInteger (info, 'timestamp');
        const fundingTimestamp = (Math.floor (this.milliseconds () / 60 / 60 / 1000) + 1) * 60 * 60 * 1000;
        return {
            'info': info,
            'symbol': symbol,
            'markPrice': markPx,
            'indexPrice': oraclePx,
            'interestRate': undefined,
            'estimatedSettlePrice': undefined,
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'fundingRate': funding,
            'fundingTimestamp': fundingTimestamp,
            'fundingDatetime': this.iso8601 (fundingTimestamp),
            'nextFundingRate': nextFundingRate,
            'nextFundingTimestamp': undefined,
            'nextFundingDatetime': undefined,
            'previousFundingRate': undefined,
            'previousFundingTimestamp': undefined,
            'previousFundingDatetime': undefined,
            'interval': '1h',
        } as FundingRate;
    }

    /**
     * @method
     * @name pacifica#fetchOHLCV
     * @description fetches historical candlestick data containing the open, high, low, and close price, and the volume of a market
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/markets/get-candle-data
     * @param {string} symbol unified symbol of the market to fetch OHLCV data for
     * @param {string} timeframe the length of time each candle represents, support '1m', '3m', '5m', '15m', '30m', '1h', '2h', '4h', '8h', '12h', '1d', '1w', '1M'
     * @param {int} [since] timestamp in ms of the earliest candle to fetch
     * @param {int} [limit] the maximum amount of candles to fetch
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.until] timestamp in ms of the latest candle to fetch. 'limit' is priority
     * @param {boolean} [params.paginate] default false, when true will automatically paginate by calling this endpoint multiple times. See in the docs all the [availble parameters](https://github.com/ccxt/ccxt/wiki/Manual#pagination-params
     * @returns {int[][]} A list of candles ordered as timestamp, open, high, low, close, volume
     */
    override async fetchOHLCV (symbol: string, timeframe: string = '1m', since: Int = undefined, limit: Int = undefined, params = {}): Promise<OHLCV[]> {
        if (since === undefined) {
            throw new ArgumentsRequired (this.id + ' fetchOHLCV() requires a "since" argument');
        }
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' fetchOHLCV() requires a "symbol" argument');
        }
        const defaultMaxLimit = 3950; // 4000 by docs, but in fact >~3960 returns error
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        let paginate = false;
        [ paginate, params ] = this.handleOptionAndParams (params, 'fetchOHLCV', 'paginate', false);
        if (paginate) {
            return await this.fetchPaginatedCallDeterministic ('fetchOHLCV', symbol, since, limit, timeframe, params, defaultMaxLimit) as OHLCV[];
        }
        const tf = this.safeString (this.timeframes, timeframe, timeframe);
        let request: Dict = {
            'symbol': market['id'],
            'interval': tf,
            'start_time': since,
        };
        [ request, params ] = this.handleUntilOption ('end_time', request, params);
        const nowMillis = this.milliseconds ();
        let until = this.safeInteger (request, 'end_time');
        if (until === undefined) {
            if (limit !== undefined) {
                until = since + (limit * (this.parseTimeframe (tf) * 1000)) - 1;
            }
            if (until === undefined) {
                until = since + (defaultMaxLimit * (this.parseTimeframe (tf) * 1000)) - 1;
            }
            if (until > nowMillis) {
                until = nowMillis;
            }
            request['end_time'] = until;
        }
        const response = await this.publicGetKline (this.extend (request, params));
        //
        // {
        //   "success": true,
        //   "data": [
        //     {
        //       "t": 1748954160000,
        //       "T": 1748954220000,
        //       "s": "BTC",
        //       "i": "1m",
        //       "o": "105376",
        //       "c": "105376",
        //       "h": "105376",
        //       "l": "105376",
        //       "v": "0.00022",
        //       "n": 2
        //     }
        //   ],
        //   "error": null,
        //   "code": null
        // }
        //
        const candles = this.safeList (response, 'data', []);
        return this.parseOHLCVs (candles, market, timeframe, since, limit);
    }

    override parseOHLCV (ohlcv: any, market: Market = undefined): OHLCV {
        //
        //     {
        //       "t": 1748954160000,
        //       "T": 1748954220000,
        //       "s": "BTC",
        //       "i": "1m",
        //       "o": "105376",
        //       "c": "105376",
        //       "h": "105376",
        //       "l": "105376",
        //       "v": "0.00022",
        //       "n": 2
        //     }
        //
        return [
            this.safeInteger (ohlcv, 't'),
            this.safeNumber (ohlcv, 'o'),
            this.safeNumber (ohlcv, 'h'),
            this.safeNumber (ohlcv, 'l'),
            this.safeNumber (ohlcv, 'c'),
            this.safeNumber (ohlcv, 'v'),
        ];
    }

    /**
     * @method
     * @name pacifica#fetchTrades
     * @description get the list of most recent trades for a particular symbol
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/markets/get-recent-trades
     * @param {string} symbol unified market symbol
     * @param {int} [since] the earliest time in ms to fetch trades for
     * @param {int} [limit] the maximum number of trades structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {Trade[]} a list of [trade structures]{@link https://docs.ccxt.com/?id=trade-structure}
     */
    override async fetchTrades (symbol: string, since: Int = undefined, limit: Int = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const request = {
            'symbol': market['id'],
        };
        const response = await this.publicGetTrades (this.extend (request, params));
        //
        // {
        //   "success": true,
        //   "data": [
        //     {
        //       "event_type": "fulfill_taker",
        //       "price": "104721",
        //       "amount": "0.0001",
        //       "side": "close_long",
        //       "cause": "normal",
        //       "created_at": 1765006315306
        //     }
        //   ],
        //   "error": null,
        //   "code": null,
        //   "last_order_id": 1557404170
        // }
        //
        const recentTrades = this.safeList (response, 'data', []);
        return this.parseTrades (recentTrades, market, since, limit);
    }

    /**
     * @method
     * @name pacifica#fetchMyTrades
     * @description fetch all trades made by the user
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/account/get-trade-history
     * @param {string} [symbol] unified market symbol
     * @param {int} [since] the earliest time in ms to fetch trades for
     * @param {int} [limit] the maximum number of trades structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.until] timestamp in ms of the latest trade
     * @param {string} [params.account] will default to walletAddress if not provided
     * @param {string} [params.cursor] pagination cursor from prev request (manual use)
     * @param {boolean} [params.paginate] default false, when true will automatically paginate by calling this endpoint multiple times. See in the docs all the [availble parameters](https://github.com/ccxt/ccxt/wiki/Manual#pagination-params)
     * @returns {Trade[]} a list of [trade structures]{@link https://docs.ccxt.com/?id=trade-structure}
     */
    override async fetchMyTrades (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<Trade[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        let market: Market = undefined;
        if (symbol !== undefined) {
            market = this.market (symbol);
        }
        let paginate = false;
        [ paginate, params ] = this.handleOptionAndParams (params, 'fetchMyTrades', 'paginate', false);
        let userAddress: Str = undefined;
        [ userAddress, params ] = this.handleOriginAndSingleAddress ('fetchMyTrades', params);
        const defaultLimit = 100;  // Default max limit
        if (paginate) {
            return await this.fetchPaginatedCallCursor ('fetchMyTrades', symbol, since, limit, params, 'next_cursor', 'cursor', undefined, defaultLimit) as Trade[];
        }
        let request: Dict = {};
        [ request, params ] = this.handleUntilOption ('end_time', request, params);
        request['account'] = userAddress;
        if (symbol !== undefined) {
            request['symbol'] = this.safeString (market, 'id');
        }
        if (limit !== undefined) {
            request['limit'] = limit;
        }
        if (since !== undefined) {
            request['start_time'] = since;
        }
        const response = await this.publicGetTradesHistory (this.extend (request, params));
        //
        // {
        //   "success": true,
        //   "data": [
        //     {
        //       "history_id": 19329801,
        //       "order_id": 315293920,
        //       "client_order_id": "acf...",
        //       "symbol": "LDO",
        //       "amount": "0.1",
        //       "price": "1.1904",
        //       "entry_price": "1.176247",
        //       "fee": "0",
        //       "pnl": "-0.001415",
        //       "event_type": "fulfill_maker",
        //       "side": "close_short",
        //       "created_at": 1759215599188,
        //       "cause": "normal"
        //     },
        //     ...
        //   ],
        //   "next_cursor": "11111Z5RK", // not included to info!
        //   "has_more": true   // not included to info!
        // }
        //
        const data = this.addPaginationCursorToResult (response);
        return this.parseTrades (data, market, since, limit);
    }

    override parseTrade (trade: Dict, market: Market = undefined): Trade {
        //
        // user trades:
        //     {
        //       "history_id": 19329801,
        //       "order_id": 315293920,
        //       "client_order_id": "acf...",
        //       "symbol": "LDO",
        //       "amount": "0.1",
        //       "price": "1.1904",
        //       "entry_price": "1.176247",
        //       "fee": "0",
        //       "pnl": "-0.001415",
        //       "event_type": "fulfill_maker",
        //       "side": "close_short",
        //       "created_at": 1759215599188,
        //       "cause": "normal"
        //     },
        // recent public trades:
        //     {
        //       "event_type": "fulfill_taker",
        //       "price": "104721",
        //       "amount": "0.0001",
        //       "side": "close_long",
        //       "cause": "normal",
        //       "created_at": 1765006315306
        //     }
        //
        const eventType = this.safeString (trade, 'event_type');
        const timestamp = this.safeInteger (trade, 'created_at');
        const price = this.safeString (trade, 'price');
        const amount = this.safeString (trade, 'amount');
        const symbol = this.safeSymbol (undefined, market);
        const id = this.safeString (trade, 'history_id');
        let side = this.safeString (trade, 'side');
        if (side === 'open_long') {
            side = 'buy';
        } else if (side === 'close_long') {
            side = 'sell';
        } else if (side === 'open_short') {
            side = 'sell';
        } else if (side === 'close_short') {
            side = 'buy';
        }
        const fee = this.safeString (trade, 'fee');
        const orderId = this.safeString (trade, 'order_id');
        let takerOrMaker: Str = undefined;
        if (eventType !== undefined) {
            takerOrMaker = (eventType === 'fulfill_maker') ? 'maker' : 'taker';
        }
        // public trades have no orderId
        if (orderId === undefined) {
            takerOrMaker = undefined;
        }
        return this.safeTrade ({
            'info': trade,
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'symbol': symbol,
            'id': id,
            'order': orderId,
            'type': undefined,
            'side': side,
            'takerOrMaker': takerOrMaker,
            'price': price,
            'amount': amount,
            'cost': undefined,
            'fee': {
                'cost': fee,
                'currency': 'USDC',
                'rate': undefined,
            },
        }, market);
    }

    /**
     * @method
     * @name pacifica#createOrder
     * @description create a trade order
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/create-limit-order
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/create-market-order
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/create-stop-order
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/create-position-tp-sl
     * @param {string} symbol unified symbol of the market to create an order in
     * @param {string} type 'market' or 'limit'
     * @param {string} side 'buy' or 'sell'
     * @param {float} amount how much of currency you want to trade in units of base currency. Not used for set tpsl order!
     * @param {float} [price] the price at which the order is to be fullfilled, in units of the quote currency, ignored in market orders
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {float} [params.triggerPrice] The price a trigger order is triggered at
     * @param {float} [params.stopLossPrice] the price that a stop loss order is triggered at (optional provide stopLossCloid)
     * @param {float} [params.takeProfitPrice] the price that a take profit order is triggered at (optional provide takeProfitCloid)
     * @param {string} [params.timeInForce] "GTC", "IOC", or "PO" or "ALO" or "PO_TOB" (or "TOB" - PO by top of book)
     * @param {boolean} [params.reduceOnly] Ensures that the executed order does not flip the opened position.
     * @param {string} [params.clientOrderId] client order id, (optional uuid v4 e.g.: f47ac10b-58cc-4372-a567-0e02b2c3d479)
     * @param {int} [params.expiryWindow] time to live in milliseconds
     * @returns {object} an [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async createOrder (symbol: string, type: OrderType, side: OrderSide, amount: number, price: Num = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        await this.initializeClient ();
        const [ request, operationType ] = this.createOrderRequest (symbol, type, side, amount, price, params);
        params = this.omit (params, [
            'reduceOnly', 'clientOrderId', 'stopLimitPrice', 'timeInForce', 'triggerPrice', 'stopLossCloid',
            'stopLossPrice', 'stopLossLimitPrice', 'takeProfitCloid', 'takeProfitPrice', 'takeProfitLimitPrice', 'expiryWindow',
        ]);
        let response = undefined;
        if (operationType === 'create_market_order') {
            response = await this.privatePostOrdersCreateMarket (this.extend (request, params));
        } else if (operationType === 'create_stop_order') {
            response = await this.privatePostOrdersStopCreate (this.extend (request, params));
        } else if (operationType === 'set_position_tpsl') {
            response = await this.privatePostPositionsTpsl (this.extend (request, params));
        } else { // create_order
            response = await this.privatePostOrdersCreate (this.extend (request, params));
        }
        //
        // {
        //   'success': true,
        //   'data': {
        //    "order_id": 12345
        //    },
        // }
        //
        const success = this.safeBool (response, 'success', false);
        let status: Str = undefined;
        if (!success) {
            status = 'rejected';
        } else {
            status = 'open';
        }
        const order = this.safeDict (response, 'data', {});
        const orderId = this.safeString (order, 'order_id');
        return this.safeOrder ({ 'id': orderId, 'status': status, 'info': response, 'symbol': symbol });
    }

    createOrderRequest (symbol: Str, type: Str, side: Str, amount: Num, price: Num = undefined, params = {}): [Dict, Str] {
        if (type === undefined) {
            throw new ArgumentsRequired (this.id + ' requires a type argument');
        }
        if (side === undefined) {
            throw new ArgumentsRequired (this.id + ' requires a side argument');
        }
        /**
         * @method
         * @ignore
         * @name pacifica#createOrderRequest
         * @description create a trade order
         * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/create-limit-order
         * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/create-market-order
         * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/create-stop-order
         * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/create-position-tp-sl
         * @param {string} symbol unified symbol of the market to create an order in
         * @param {string} type 'market' or 'limit'
         * @param {string} side 'buy' or 'sell'
         * @param {float} amount how much of currency you want to trade in units of base currency
         * @param {float} [price] the price at which the order is to be fullfilled, in units of the quote currency, ignored in market orders, but can be used as limit_price of Trigger Order.
         * @param {object} [params] extra parameters specific to the exchange API endpoint
         * @param {float} [params.triggerPrice] The price a trigger order is triggered at
         * @param {float} [params.stopLossPrice] the price that a stop loss order is triggered at (optional provide stopLossCloid)
         * @param {float} [params.takeProfitPrice] the price that a take profit order is triggered at (optional provide takeProfitCloid)
         * @param {string} [params.timeInForce] "GTC", "IOC", or "PO" or "ALO" or "PO_TOB" (or "TOB" - PO by top of book)
         * @param {boolean} [params.reduceOnly] Ensures that the executed order does not flip the opened position.
         * @param {string} [params.clientOrderId] client order id, (optional uuid v4 e.g.: f47ac10b-58cc-4372-a567-0e02b2c3d479)
         * @param {int} [params.expiryWindow] time to live in milliseconds
         * @returns {object} an [order structure]
         */
        const market = this.market (symbol);
        const sigPayload: Dict = {
            'symbol': market['id'],
            'side': this.mapSide (side),
        };
        let operationType: Str = undefined;
        const reduceOnly = this.safeBool2 (params, 'reduceOnly', 'reduce_only', false);
        const orderType = type.toUpperCase ();
        const triggerPrice = this.safeString (params, 'triggerPrice');
        const stopLossPrice = this.safeString (params, 'stopLossPrice');
        const takeProfitPrice = this.safeString (params, 'takeProfitPrice');
        const tifRaw = this.safeStringUpper (params, 'timeInForce');
        const isMarket = orderType === 'MARKET';
        const isTakeProfitOrder = (takeProfitPrice !== undefined);
        const isStopLossOrder = (stopLossPrice !== undefined);
        const isStopOrder = (triggerPrice !== undefined);
        const timeInForce = this.mapTimeInForce (tifRaw);
        if (isMarket) {
            operationType = 'create_market_order';
            sigPayload['reduce_only'] = reduceOnly;
            const defaultSlippage = this.handleOption ('createOrder', 'defaultSlippage', '0.5');
            const slippage = this.safeString2 (params, 'slippage', 'slippage_percent', defaultSlippage);
            sigPayload['slippage_percent'] = slippage;
        } else if ((isTakeProfitOrder || isStopLossOrder) && (price === undefined)) { // the tpsl endpoint does not accept a 'price' parameter
            operationType = 'set_position_tpsl';
        } else if (isStopOrder) {
            operationType = 'create_stop_order';
            sigPayload['reduce_only'] = reduceOnly;
            const stopClientOrderId = this.safeString (params, 'clientOrderId');
            params = this.omit (params, [ 'clientOrderId' ]);
            const stopPayload: Dict = {
                'amount': this.amountToPrecision (symbol, amount),
                'stop_price': this.priceToPrecision (symbol, triggerPrice),
            };
            if (stopClientOrderId !== undefined) {
                stopPayload['client_order_id'] = stopClientOrderId;
            }
            if (price !== undefined) {
                stopPayload['limit_price'] = this.priceToPrecision (symbol, price);
            }
            sigPayload['stop_order'] = stopPayload;
        } else {
            operationType = 'create_order';
            sigPayload['reduce_only'] = reduceOnly;
            if (timeInForce === undefined) {
                sigPayload['tif'] = 'GTC';
            } else {
                sigPayload['tif'] = timeInForce;
            }
        }
        if (isTakeProfitOrder) {
            const tpPayload: Dict = {
                'stop_price': this.priceToPrecision (symbol, takeProfitPrice),
            };
            if (price !== undefined) {
                tpPayload['limit_price'] = this.priceToPrecision (symbol, price);
            }
            sigPayload['take_profit'] = tpPayload;
        }
        if (isStopLossOrder) {
            const slPayload: Dict = {
                'stop_price': this.priceToPrecision (symbol, stopLossPrice),
            };
            if (price !== undefined) {
                slPayload['limit_price'] = this.priceToPrecision (symbol, price);
            }
            sigPayload['stop_loss'] = slPayload;
        }
        if (price !== undefined && operationType === 'create_order') {
            sigPayload['price'] = this.priceToPrecision (symbol, price);
        }
        if (amount !== undefined && (operationType !== 'create_stop_order' && operationType !== 'set_position_tpsl')) {
            sigPayload['amount'] = this.amountToPrecision (symbol, amount);
        }
        const clientOrderId = this.safeString (params, 'clientOrderId');
        if (clientOrderId !== undefined) {
            sigPayload['client_order_id'] = clientOrderId;
        }
        const request = this.postActionRequest (operationType, sigPayload, params);
        return [ request, operationType ];
    }

    batchOrdersRequest (actions: any[]) {
        //
        // [
        //     {
        //         "type":"Create",
        //         "data":{
        //             "account":"42trU9A5...",
        //             "signature":"5UpRZ14Q...",
        //             "timestamp":1749190500355,
        //             "expiry_window":5000,
        //             "symbol":"BTC",
        //             "price":"100000",
        //             "reduce_only":false,
        //             "amount":"0.1",
        //             "side":"bid",
        //             "tif":"GTC",
        //             "client_order_id":"57a5efb1-bb96-49a5-8bfd-f25d5f22bc7e"
        //         }
        //     },
        //     {
        //         "type":"Cancel",
        //         "data":{
        //             "account":"42trU9A5...",
        //             "signature":"4NDFHyTG...",
        //             "timestamp":1749190500355,
        //             "expiry_window":5000,
        //             "symbol":"BTC",
        //             "order_id":42069
        //         }
        //     }
        // ]
        //
        //  Create (Only Limit or Market, never stop order or tpsl order)
        //  Cancel (Only common (limit) orders)
        //
        const lenActions = actions.length;
        const maxLen = this.handleOption ('batchOrdersRequest', 'batchOrdersMax');
        if (maxLen !== undefined) {
            if (lenActions > maxLen) {
                throw new ExchangeError (this.id + ' batchOrdersRequest() too many orders to create/cancel. Limit is ' + maxLen);
            }
        }
        return {
            'actions': actions,
        };
    }

    createOrdersRequest (orders: OrderRequest[], params = {}) {
        const actions: Dict[] = [];
        const timestamp = this.milliseconds (); // unified sequence
        for (let i = 0; i < orders.length; i++) {
            const order = orders[i];
            const symbol = this.safeString (order, 'symbol');
            const side = this.safeString (order, 'side');
            const price = this.safeString (order, 'price');
            const type = this.safeString (order, 'type', 'limit');
            const orderParams = this.safeDict (order, 'params', {});
            orderParams['timestamp'] = timestamp;
            const amount = this.safeString (order, 'amount');
            const amountNumber = this.parseNumber (amount);
            const priceNumber = this.parseNumber (price);
            if (type !== 'limit') {
                throw new NotSupported (this.id + ' createOrders() supports only type = "limit"! Your value type=' + type);
            }
            const requestList = this.createOrderRequest (symbol, type, side, amountNumber, priceNumber, orderParams);
            const action = {
                'type': 'Create',
                'data': requestList[0],
            };
            actions.push (action);
        }
        return this.batchOrdersRequest (actions);
    }

    /**
     * @method
     * @name pacifica#createOrders
     * @description create a list of trade orders. It is supports only limit orders and have a random jitter ~100-300ms!
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/batch-order
     * @param {Array} orders list of orders to create, each object should contain the parameters required by createOrder, namely symbol, type (optional or 'limit'), side, amount, price and params
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} an [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async createOrders (orders: OrderRequest[], params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        await this.initializeClient ();
        const request = this.createOrdersRequest (orders);
        const response = await this.privatePostOrdersBatch (this.extend (request, params));
        // {
        //   "success": true,
        //   "data": {
        //     "results": [
        //       {
        //         "success": true,
        //         "order_id": 470506,
        //         "error": null
        //       },
        //       {
        //         "success": true,
        //       }
        //     ]
        //   },
        //     "error": null,
        //     "code": null
        // }
        //
        const data = this.safeDict (response, 'data', {});
        const results = this.safeList (data, 'results', []);
        const ordersToReturn: Order[] = [];
        for (let i = 0; i < results.length; i++) {
            const order = results[i];
            const error = this.safeString (order, 'error');
            const success = this.safeBool (order, 'success', false);
            let status: Str = undefined;
            if ((error !== undefined) || (!success)) {
                status = 'rejected';
            } else {
                status = 'open';
            }
            const orderId = this.safeString (order, 'order_id');
            ordersToReturn.push (this.safeOrder ({ 'info': order, 'id': orderId, 'status': status }));
        }
        return ordersToReturn as Order[];
    }

    /**
     * @method
     * @name pacifica#cancelOrders
     * @description cancel multiple orders
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/batch-order
     * @param {string[]} ids order ids. An ids list is always required (can be empty). Both ids and clientOrderIds can be passed simultaneously.
     * @param {string} [symbol] unified market symbol
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string|string[]} [params.clientOrderIds] client order ids, (optional uuid v4 e.g.: f47ac10b-58cc-4372-a567-0e02b2c3d479)
     * @param {int} [params.expiryWindow] time to live in milliseconds
     * @returns {object} an list of [order structures]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async cancelOrders (ids: string[], symbol: Str = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        await this.initializeClient ();
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' cancelOrders() requires a "symbol" argument!');
        }
        const request = this.cancelOrdersRequest (ids, symbol, params);
        params = this.omit (params, [ 'expiryWindow', 'clientOrderIds' ]);
        const response = await this.privatePostOrdersBatch (this.extend (request, params));
        //
        // {
        //   "success": true,
        //   "data": {
        //     "results": [
        //       {
        //         "success": true,
        //         "order_id": 470506,
        //         "error": null
        //       },
        //       {
        //         "success": true,
        //       }
        //     ]
        //   },
        //     "error": null,
        //     "code": null
        // }
        //
        const data = this.safeDict (response, 'data', {});
        const results = this.safeList (data, 'results', []);
        const ordersToReturn: Order[] = [];
        for (let i = 0; i < results.length; i++) {
            const order = results[i];
            const error = this.safeString (order, 'error');
            const success = this.safeBool (order, 'success', false);
            let status: Str = undefined;
            if ((error !== undefined) || (!success)) {
                status = 'closed';
            } else {
                status = 'canceled';
            }
            ordersToReturn.push (this.safeOrder ({ 'info': order, 'status': status, 'symbol': symbol }));
        }
        return ordersToReturn as Order[];
    }

    cancelOrdersRequest (ids: Str[], symbol: Str = undefined, params = {}) {
        const actions: Dict[] = [];
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const request = this.cancelOrderRequest (id, symbol, params);
            const action = {
                'type': 'Cancel',
                'data': request,
            };
            actions.push (action);
        }
        const clientOrderIds = this.safeList (params, 'clientOrderIds', []);
        params = this.omit (params, 'clientOrderIds');
        for (let i = 0; i < clientOrderIds.length; i++) {
            const cloid = clientOrderIds[i];
            const cloidParams = {
                'clientOrderId': cloid,
            };
            const request = this.cancelOrderRequest (cloid, symbol, this.extend (cloidParams, params));
            const action = {
                'type': 'Cancel',
                'data': request,
            };
            actions.push (action);
        }
        return this.batchOrdersRequest (actions);
    }

    /**
     * @method
     * @name pacifica#cancelAllOrders
     * @description cancel all open orders in a market
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/cancel-all-orders
     * @param {string} [symbol] (optional) unified market symbol of the market to cancel orders in.
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {boolean} [params.excludeReduceOnly] whether to exclude reduce-only orders
     * @param {int} [params.expiryWindow] time to live in milliseconds
     * @returns {object[]} a list of [order structures]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async cancelAllOrders (symbol: Str = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        await this.initializeClient ();
        const request = this.cancelAllOrdersRequest (symbol, params);
        params = this.omit (params, [ 'excludeReduceOnly', 'expiryWindow' ]);
        const response = await this.privatePostOrdersCancelAll (this.extend (request, params));
        //
        // {
        //   success: true,
        //   data: {
        //     "cancelled_count": 5,
        //   },
        //   code: null,
        //   error: null
        // }
        //
        return [
            this.safeOrder ({
                'info': response,
            }),
        ] as Order[];
    }

    cancelAllOrdersRequest (symbol: Str, params = {}) {
        const operationType = 'cancel_all_orders';
        const sigPayload: Dict = { };
        const excludeReduceOnly = this.safeBool (params, 'excludeReduceOnly', false);
        sigPayload['exclude_reduce_only'] = excludeReduceOnly;
        if (symbol !== undefined) {
            const market = this.market (symbol);
            sigPayload['all_symbols'] = false;
            sigPayload['symbol'] = market['id'];
        } else {
            sigPayload['all_symbols'] = true;
        }
        const request = this.postActionRequest (operationType, sigPayload, params);
        return request;
    }

    /**
     * @method
     * @name pacifica#cancelOrder
     * @description cancels an open order
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/cancel-stop-order#response
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/cancel-order
     * @param {string} id order id
     * @param {string} symbol unified symbol of the market the order was made in
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {boolean} [params.stop] necessary if this is to cancel a stop order.
     * @param {string} [params.clientOrderId] client order id, (optional uuid v4 e.g.: f47ac10b-58cc-4372-a567-0e02b2c3d479)
     * @param {int} [params.expiryWindow] time to live in milliseconds
     * @returns {object} An [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async cancelOrder (id: string, symbol: Str = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        await this.initializeClient ();
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' cancelOrder() requires a symbol argument');
        }
        const request = this.cancelOrderRequest (id, symbol, params);
        const isStopOrder = this.safeBool2 (params, 'trigger', 'stop', false);
        params = this.omit (params, [ 'expiryWindow', 'trigger', 'stop', 'clientOrderId' ]);
        let response = undefined;
        if (isStopOrder) {
            response = await this.privatePostOrdersStopCancel (this.extend (request, params));
        } else {
            response = await this.privatePostOrdersCancel (this.extend (request, params));
        }
        //
        // response:
        // {
        //   "success": true,
        //   "data": null
        // }
        //
        const success = this.safeBool (response, 'success', false);
        const status = success ? 'canceled' : 'closed';
        return this.safeOrder ({ 'id': id, 'status': status, 'info': response, 'symbol': symbol });
    }

    cancelOrderRequest (id: Str, symbol: Str = undefined, params = {}) {
        const market = this.market (symbol);
        const isStopOrder = this.safeBool2 (params, 'trigger', 'stop', false);
        let operationType: Str = undefined;
        if (isStopOrder) {
            operationType = 'cancel_stop_order';
        } else {
            operationType = 'cancel_order';
        }
        const clientOrderId = this.safeString (params, 'clientOrderId');
        const sigPayload: Dict = {
            'symbol': market['id'],
        };
        if (clientOrderId !== undefined) {
            sigPayload['client_order_id'] = clientOrderId;
        } else {
            sigPayload['order_id'] = this.parseToInt (id);
        }
        const request = this.postActionRequest (operationType, sigPayload, params);
        return request;
    }

    /**
     * @method
     * @name pacifica#editOrder
     * @description edit a trade order
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/edit-order
     * @param {string} id edit order id
     * @param {string} symbol unified symbol of the market to edit an order in
     * @param {string} type 'market' or 'limit' WARN is not usable!
     * @param {string} side 'buy' or 'sell' WARN is not usable!
     * @param {float} amount how much of currency you want to trade in units of base currency
     * @param {float} price the price at which the order is to be fulfilled, in units of the quote currency
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.clientOrderId] client order id, (optional uuid v4 e.g.: f47ac10b-58cc-4372-a567-0e02b2c3d479)
     * @param {int} [params.expiryWindow] time to live in milliseconds
     * @returns {object} an [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async editOrder (id: string, symbol: string, type: OrderType, side: OrderSide, amount: Num = undefined, price: Num = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        await this.initializeClient ();
        const market = this.market (symbol);
        const request = this.editOrderRequest (id, symbol, type, side, amount, price, market, params);
        params = this.omit (params, [ 'expiryWindow', 'clientOrderId' ]);
        const response = await this.privatePostOrdersEdit (this.extend (request, params));
        //
        // {
        //     'data': {
        //         "order_id": 123498765
        //     }
        // }
        //
        const data = this.safeDict (response, 'data', {});
        const orderId = this.safeString (data, 'order_id');
        return this.safeOrder ({ 'id': orderId, 'info': response, 'symbol': symbol });
    }

    editOrderRequest (id: string, symbol: Str, type: string, side: Str, amount: Num, price: Num, market: Market, params = {}) {
        if (side === undefined) {
            throw new ArgumentsRequired (this.id + ' requires a side argument');
        }
        if (amount === undefined) {
            throw new ArgumentsRequired (this.id + ' editOrder() requires an amount!');
        }
        if (price === undefined) {
            throw new ArgumentsRequired (this.id + ' editOrder() requires a price');
        }
        const operationType = 'edit_order';
        const clientOrderId = this.safeString (params, 'clientOrderId');
        const priceNormalized = this.priceToPrecision (symbol, price);
        const amountNormalized = this.amountToPrecision (symbol, amount);
        const sigPayload: Dict = {
            'symbol': this.safeString (market, 'id'),
            'price': priceNormalized,
            'amount': amountNormalized,
        };
        if ((clientOrderId === undefined) && (id === undefined)) {
            throw new ArgumentsRequired ('this.id' + 'editOrder() requires either "id" or "clientOrderId"');
        }
        if (clientOrderId !== undefined) {
            sigPayload['client_order_id'] = clientOrderId;
        } else {
            sigPayload['order_id'] = this.parseToInt (id);
        }
        const request = this.postActionRequest (operationType, sigPayload, params);
        return request;
    }

    /**
     * @method
     * @name pacifica#fetchFundingRateHistory
     * @description fetches historical funding rate prices
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/markets/get-historical-funding
     * @param {string} symbol unified symbol of the market to fetch the funding rate history for
     * @param {int} [since] timestamp in ms of the earliest funding rate to fetch
     * @param {int} [limit] the maximum amount of [funding rate structures]{@link https://docs.ccxt.com/?id=funding-rate-history-structure} to fetch
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.cursor] pagination cursor from prev request (manual use)
     * @param {boolean} [params.paginate] default false, when true will automatically paginate by calling this endpoint multiple times. See in the docs all the [availble parameters](https://github.com/ccxt/ccxt/wiki/Manual#pagination-params)
     * @returns {object[]} a list of [funding rate structures]{@link https://docs.ccxt.com/?id=funding-rate-history-structure}
     */
    override async fetchFundingRateHistory (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' fetchFundingRateHistory() requires a symbol argument');
        }
        const market = this.market (symbol);
        let paginate = false;
        [ paginate, params ] = this.handleOptionAndParams (params, 'fetchFundingRateHistory', 'paginate', false);
        const defaultLimit = 100;  // Default max limit
        if (paginate) {
            return await this.fetchPaginatedCallCursor ('fetchFundingRateHistory', symbol, since, limit, params, 'next_cursor', 'cursor', undefined, defaultLimit) as FundingRateHistory[];
        }
        const request: Dict = {
            'symbol': market['id'],
        };
        if (limit !== undefined) {
            request['limit'] = limit;
        }
        const response = await this.publicGetFundingRateHistory (this.extend (request, params));
        //
        // {
        //   "success": true,
        //   "data": [
        //     {
        //       "oracle_price": "117170.410304",
        //       "bid_impact_price": "117126",
        //       "ask_impact_price": "117142",
        //       "funding_rate": "0.0000125",
        //       "next_funding_rate": "0.0000125",
        //       "created_at": 1753806934249
        //     },
        //     ...
        //   ],
        //   "next_cursor": "11114Lz77",
        //   "has_more": true
        // }
        //
        const data = this.addPaginationCursorToResult (response);
        const result: FundingRateHistory[] = [];
        for (let i = 0; i < data.length; i++) {
            const entry = data[i];
            const timestamp = this.safeInteger (entry, 'created_at');
            result.push ({
                'info': entry,
                'symbol': market['symbol'],
                'fundingRate': this.safeNumber (entry, 'funding_rate'),
                'timestamp': timestamp,
                'datetime': this.iso8601 (timestamp),
            });
        }
        const sorted = this.sortBy (result, 'timestamp');
        return this.filterBySinceLimit (sorted, since, limit, 'timestamp') as FundingRateHistory[];
    }

    /**
     * @method
     * @name pacifica#fetchTickers
     * @description fetches price tickers for multiple markets, statistical information calculated over the past 24 hours for each market
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/markets/get-prices
     * @param {string[]} [symbols] unified symbols of the markets to fetch the ticker for, all market tickers are returned if not assigned
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a dictionary of [ticker structures]{@link https://docs.ccxt.com/?id=ticker-structure}
     */
    override async fetchTickers (symbols: Strings = undefined, params = {}): Promise<Tickers> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        symbols = this.marketSymbols (symbols);
        const response = await this.publicGetInfoPrices (params);
        //
        //  {
        //   "success": true,
        //   "data": [
        //     {
        //       "funding": "0.00010529",
        //       "mark": "1.084819",
        //       "mid": "1.08615",
        //       "next_funding": "0.00011096",
        //       "open_interest": "3634796",
        //       "oracle": "1.084524",
        //       "symbol": "XPL",
        //       "timestamp": 1759222967974,
        //       "volume_24h": "20896698.0672",
        //       "yesterday_price": "1.3412"
        //     }
        //   ],
        //   "error": null,
        //   "code": null
        // }
        //
        const data = this.safeList (response, 'data', []);
        const result: Dict = {};
        for (let i = 0; i < data.length; i++) {
            const info = data[i];
            const ticker = this.parseTicker (info);
            const symbol = this.safeString (ticker, 'symbol');
            if (symbol !== undefined) {
                result[symbol] = ticker;
            }
        }
        return this.filterByArrayTickers (result, 'symbol', symbols);
    }

    override parseTicker (ticker: Dict, market: Market = undefined): Ticker {
        //
        //     {
        //       "funding": "0.00010529",
        //       "mark": "1.084819",
        //       "mid": "1.08615",
        //       "next_funding": "0.00011096",
        //       "open_interest": "3634796",
        //       "oracle": "1.084524",
        //       "symbol": "XPL",
        //       "timestamp": 1759222967974,
        //       "volume_24h": "20896698.0672",
        //       "yesterday_price": "1.3412"
        //     }
        //
        const marketId = this.safeString (ticker, 'symbol');
        market = this.safeMarket (marketId, market);
        const symbol = market['symbol'];
        const timestamp = this.safeInteger (ticker, 'timestamp');
        return this.safeTicker ({
            'symbol': symbol,
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'previousClose': this.safeNumber (ticker, 'yesterday_price'),
            'close': this.safeNumber (ticker, 'mid'),
            'bid': undefined,
            'ask': undefined,
            'quoteVolume': this.safeNumber (ticker, 'volume_24h'),
            'info': ticker,
        }, market);
    }

    /**
     * @method
     * @name pacifica#fetchClosedOrders
     * @description fetch all unfilled currently closed orders
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/get-order-history
     * @param {string} symbol unified market symbol
     * @param {int} [since] the earliest time in ms to fetch open orders for
     * @param {int} [limit] the maximum number of open orders structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @returns {Order[]} a list of [order structures]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async fetchClosedOrders (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<Order[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const orders = await this.fetchOrders (symbol, undefined, undefined, params); // don't filter here because we don't want to catch open orders
        const closedOrders = this.filterByArray (orders, 'status', [ 'closed' ], false);
        return this.filterBySymbolSinceLimit (closedOrders, symbol, since, limit) as Order[];
    }

    /**
     * @method
     * @name pacifica#fetchCanceledOrders
     * @description fetch all canceled orders
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/get-order-history
     * @param {string} symbol unified market symbol
     * @param {int} [since] the earliest time in ms to fetch open orders for
     * @param {int} [limit] the maximum number of open orders structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @returns {Order[]} a list of [order structures]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async fetchCanceledOrders (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<Order[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const orders = await this.fetchOrders (symbol, undefined, undefined, params); // don't filter here because we don't want to catch open orders
        const closedOrders = this.filterByArray (orders, 'status', [ 'canceled' ], false);
        return this.filterBySymbolSinceLimit (closedOrders, symbol, since, limit) as Order[];
    }

    /**
     * @method
     * @name pacifica#fetchCanceledAndClosedOrders
     * @description fetch all closed and canceled orders
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/get-order-history
     * @param {string} symbol unified market symbol
     * @param {int} [since] the earliest time in ms to fetch open orders for
     * @param {int} [limit] the maximum number of open orders structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @returns {Order[]} a list of [order structures]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async fetchCanceledAndClosedOrders (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<Order[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const orders = await this.fetchOrders (symbol, undefined, undefined, params); // don't filter here because we don't want to catch open orders
        const closedOrders = this.filterByArray (orders, 'status', [ 'canceled', 'closed', 'rejected' ], false);
        return this.filterBySymbolSinceLimit (closedOrders, symbol, since, limit) as Order[];
    }

    /**
     * @method
     * @name pacifica#fetchOpenOrders
     * @description fetch all unfilled currently open orders
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/get-open-orders
     * @param {string} symbol unified market symbol
     * @param {int} [since] the earliest time in ms to fetch open orders for
     * @param {int} [limit] the maximum number of open orders structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @returns {Order[]} a list of [order structures]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async fetchOpenOrders (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<Order[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        let userAddress: Str = undefined;
        [ userAddress, params ] = this.handleOriginAndSingleAddress ('fetchOpenOrders', params);
        const request: Dict = {
            'account': userAddress,
        };
        let market: Market = undefined;
        if (symbol !== undefined) {
            market = this.market (symbol);
        }
        const response = await this.publicGetOrders (this.extend (request, params));
        //
        // {
        //   "success": true,
        //   "data": [
        //     {
        //       "order_id": 315979358,
        //       "client_order_id": "add9a4b5-c7f7-4124-b57f-86982d86d479",
        //       "symbol": "ASTER",
        //       "side": "ask",
        //       "price": "1.836",
        //       "initial_amount": "85.33",
        //       "filled_amount": "0",
        //       "cancelled_amount": "0",
        //       "stop_price": null,
        //       "order_type": "limit",
        //       "stop_parent_order_id": null,
        //       "reduce_only": false,
        //       "created_at": 1759224706737,
        //       "updated_at": 1759224706737
        //     }
        //   ],
        //   "error": null,
        //   "code": null,
        //   "last_order_id": 1557370337
        // }
        //
        const data = this.safeList (response, 'data', []);
        return this.parseOrders (data, market, since, limit);
    }

    /**
     * @method
     * @name pacifica#fetchOrders
     * @description fetch all orders
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/get-order-history
     * @param {string} symbol unified market symbol
     * @param {int} [since] the earliest time in ms to fetch open orders for
     * @param {int} [limit] the maximum number of open orders structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @param {string} [params.cursor] pagination cursor from prev request (manual use)
     * @param {boolean} [params.paginate] default false, when true will automatically paginate by calling this endpoint multiple times. See in the docs all the [availble parameters](https://github.com/ccxt/ccxt/wiki/Manual#pagination-params)
     * @returns {Order[]} a list of [order structures]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async fetchOrders (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<Order[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        let paginate = false;
        [ paginate, params ] = this.handleOptionAndParams (params, 'fetchOrders', 'paginate', false);
        const defaultLimit = 100; // max default 100
        if (paginate) {
            return await this.fetchPaginatedCallCursor ('fetchOrders', symbol, since, limit, params, 'next_cursor', 'cursor', undefined, defaultLimit) as Order[];
        }
        let userAddress: Str = undefined;
        [ userAddress, params ] = this.handleOriginAndSingleAddress ('fetchOrders', params);
        let market: Market = undefined;
        if (symbol !== undefined) {
            market = this.market (symbol);
        }
        const request: Dict = {
            'account': userAddress,
        };
        if (limit !== undefined) {
            request['limit'] = limit;
        }
        const response = await this.publicGetOrdersHistory (this.extend (request, params));
        //
        // {
        //   "success": true,
        //   "data": [
        //     {
        //       "order_id": 315992721,
        //       "client_order_id": "ade",
        //       "symbol": "XPL",
        //       "side": "ask",
        //       "initial_price": "1.0865",
        //       "average_filled_price": "0",
        //       "amount": "984",
        //       "filled_amount": "0",
        //       "order_status": "open",
        //       "order_type": "limit",
        //       "stop_price": null,
        //       "stop_parent_order_id": null,
        //       "reduce_only": false,
        //       "reason": null,
        //       "created_at": 1759224893638,
        //       "updated_at": 1759224893638
        //     },
        //     ...
        //   ],
        //   "next_cursor": "1111Hyd74",
        //   "has_more": true
        // }
        //
        const data = this.addPaginationCursorToResult (response);
        const orders = this.parseOrders (data, market, since, limit);
        return orders as Order[];
    }

    addPaginationCursorToResult (response: any) {
        const data = this.safeList (response, 'data', []);
        const paginationCursor = this.safeString (response, 'next_cursor');
        const hasMore = this.safeBool (response, 'has_more', false);
        const dataLength = data.length;
        if (hasMore) {
            if ((paginationCursor !== undefined) && (dataLength > 0)) {
                const first = data[0];
                first['next_cursor'] = paginationCursor;
                first['has_more'] = hasMore;
                data[0] = first;
            }
        }
        return data;
    }

    /**
     * @method
     * @name pacifica#fetchOrder
     * @description fetches information on an order made by the user
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/orders/get-order-history-by-id
     * @param {string} id order id
     * @param {string} symbol (optional) unified symbol of the market the order was made in
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} An [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async fetchOrder (id: string, symbol: Str = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        let market: Market = undefined;
        if (symbol !== undefined) {
            market = this.market (symbol);
        }
        const request: Dict = {
            'order_id': id,
        };
        const response = await this.publicGetOrdersHistoryById (this.extend (request, params));
        //
        // {
        //   "success": true,
        //   "data": [
        //     {
        //       "history_id": 641452639,
        //       "order_id": 315992721,
        //       "client_order_id": "ade1aa6...",
        //       "symbol": "XPL",
        //       "side": "ask",
        //       "price": "1.0865",
        //       "initial_amount": "984",
        //       "filled_amount": "0",
        //       "cancelled_amount": "984",
        //       "event_type": "cancel",
        //       "order_type": "limit",
        //       "order_status": "cancelled",
        //       "stop_price": null,
        //       "stop_parent_order_id": null,
        //       "reduce_only": false,
        //       "created_at": 1759224895038
        //     },
        //     {
        //       "history_id": 641452513,
        //       "order_id": 315992721,
        //       "client_order_id": "ade1aa6...",
        //       "symbol": "XPL",
        //       "side": "ask",
        //       "price": "1.0865",
        //       "initial_amount": "984",
        //       "filled_amount": "0",
        //       "cancelled_amount": "0",
        //       "event_type": "make",
        //       "order_type": "limit",
        //       "order_status": "open",
        //       "stop_price": null,
        //       "stop_parent_order_id": null,
        //       "reduce_only": false,
        //       "created_at": 1759224893638
        //     }
        //   ],
        //   "error": null,
        //   "code": null
        // }
        //
        const data = this.safeList (response, 'data', []);
        // return last state
        const sorted = this.sortBy (data, 'created_at');
        const lastIdx = sorted.length;
        let lastInfo = {};
        if (lastIdx > 0) {
            lastInfo = sorted[0];
        }
        return this.parseOrder (lastInfo, market);
    }

    parseOrderStatus (status: Str) {
        const statuses: Dict = {
            'open': 'open',
            'partially_filled': 'open',
            'filled': 'closed',
            'cancelled': 'canceled',
            'rejected': 'failed',
        };
        return this.safeString (statuses, status, status);
    }

    mapTimeInForce (tifRaw: Str) {
        const tifMap: Dict = {
            'GTC': 'GTC',
            'IOC': 'IOC',
            'PO': 'ALO',
            'POST_ONLY': 'ALO',
            'PO_TOB': 'TOB',
            'TOB': 'TOB',
            'ALO': 'ALO',
        };
        let tif: Str = undefined;
        if (tifRaw !== undefined) {
            tif = tifRaw.toUpperCase ();
        }
        return this.safeString (tifMap, tif);
    }

    mapSide (sideRaw: Str) {
        const sideMap: Dict = {
            'sell': 'ask',
            'buy': 'bid',
        };
        return this.safeString (sideMap, sideRaw, sideRaw);
    }

    parseOrderType (status: Str) {
        const statuses: Dict = {
            'stop_limit': 'limit',
            'stop_market': 'market',
            'take_profit_limit': 'limit',
            'stop_loss_limit': 'limit',
            'take_profit_market': 'market',
            'stop_loss_market': 'market',
        };
        return this.safeString (statuses, status, status);
    }

    override parseOrder (order: Dict, market: Market = undefined): Order {
        //
        // fetchOpenOrders
        //   [
        //     {
        //       "order_id": 315979358,
        //       "client_order_id": "add9a4b5-c7f7-4124-b57f-86982d86d479",
        //       "symbol": "ASTER",
        //       "side": "ask",
        //       "price": "1.836",
        //       "initial_amount": "85.33",
        //       "filled_amount": "0",
        //       "cancelled_amount": "0",
        //       "stop_price": null,
        //       "order_type": "limit",
        //       "stop_parent_order_id": null,
        //       "reduce_only": false,
        //       "created_at": 1759224706737,
        //       "updated_at": 1759224706737
        //     }
        //   ],
        //
        // fetchOrders
        //  [
        //     {
        //       "order_id": 315992721,
        //       "client_order_id": "ade",
        //       "symbol": "XPL",
        //       "side": "ask",
        //       "initial_price": "1.0865",
        //       "average_filled_price": "0",
        //       "amount": "984",
        //       "filled_amount": "0",
        //       "order_status": "open",
        //       "order_type": "limit",
        //       "stop_price": null,
        //       "stop_parent_order_id": null,
        //       "reduce_only": false,
        //       "reason": null,
        //       "created_at": 1759224893638,
        //       "updated_at": 1759224893638
        //     },
        //  ]
        //
        // fetchOrder
        //     {
        //       "history_id": 641452639,
        //       "order_id": 315992721,
        //       "client_order_id": "ade1aa6...",
        //       "symbol": "XPL",
        //       "side": "ask",
        //       "price": "1.0865",
        //       "initial_amount": "984",
        //       "filled_amount": "0",
        //       "cancelled_amount": "984",
        //       "event_type": "cancel",
        //       "order_type": "limit",
        //       "order_status": "cancelled",
        //       "stop_price": null,
        //       "stop_parent_order_id": null,
        //       "reduce_only": false,
        //       "created_at": 1759224895038
        //     }
        //
        // websocket watchOrders
        //     {
        //       "i": 1559665358,
        //       "I": null,
        //       "u": "BrZp5bidJ3WUvceSq7X78bhjTfZXeezzGvGEV4hAYKTa",
        //       "s": "BTC",
        //       "d": "bid",
        //       "p": "89501",
        //       "ip": "89501",
        //       "lp": "89501",
        //       "a": "0.00012",
        //       "f": "0.00012",
        //       "oe": "fulfill_limit",
        //       "os": "filled",
        //       "ot": "limit",
        //       "sp": null,
        //       "si": null,
        //       "r": false,
        //       "ct": 1765017049008,
        //       "ut": 1765017219639,
        //       "li": 1559696133
        //     }
        //
        const marketId = this.safeString2 (order, 'symbol', 's');
        let symbol: Str = undefined;
        if (symbol !== undefined) {
            market = this.safeMarket (marketId, market);
            symbol = market['symbol'];
        }
        const timestamp = this.safeInteger2 (order, 'created_at', 'ct');
        const status = this.safeString2 (order, 'order_status', 'os', 'open'); // open if method is fetchOpenOrders
        let side = this.safeString (order, 'side', 'd');
        if (side !== undefined) {
            side = (side === 'bid') ? 'buy' : 'sell';
        }
        const totalAmount = this.safeString2 (order, 'initial_amount', 'a');
        const filledAmount = this.safeString2 (order, 'filled_amount', 'f');
        const remaining = Precise.stringSub (totalAmount, filledAmount);
        return this.safeOrder ({
            'info': order,
            'id': this.safeString2 (order, 'order_id', 'i'),
            'clientOrderId': this.safeString2 (order, 'client_order_id', 'I'),
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'lastTradeTimestamp': undefined,
            'lastUpdateTimestamp': this.safeInteger2 (order, 'updated_at', 'ut'),
            'symbol': symbol,
            'type': this.parseOrderType (this.safeStringLower2 (order, 'order_type', 'ot')),
            'timeInForce': undefined,
            'postOnly': undefined,
            'reduceOnly': this.safeBool2 (order, 'reduce_only', 'r'),
            'side': side,
            'price': this.safeString2 (order, 'price', 'lp'),
            'triggerPrice': this.safeNumber2 (order, 'stop_price', 'sp'),
            'amount': totalAmount,
            'cost': undefined,
            'average': this.safeString2 (order, 'average_filled_price', 'p'),
            'filled': filledAmount,
            'remaining': remaining,
            'status': this.parseOrderStatus (status),
            'fee': undefined,
            'trades': undefined,
        }, market);
    }

    /**
     * @method
     * @name pacifica#fetchPosition
     * @description fetch data on an open position
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/account/get-positions
     * @param {string} symbol unified market symbol of the market the position is held in
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @returns {object} a [position structure]{@link https://docs.ccxt.com/?id=position-structure}
     */
    override async fetchPosition (symbol: string, params = {}) {
        const positions = await this.fetchPositions ([ symbol ], params);
        return this.safeDict (positions, 0, {}) as Position;
    }

    /**
     * @method
     * @name pacifica#fetchPositions
     * @description fetch all open positions
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/account/get-positions
     * @param {string[]} [symbols] list of unified market symbols
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @returns {object[]} a list of [position structure]{@link https://docs.ccxt.com/?id=position-structure}
     */
    override async fetchPositions (symbols: Strings = undefined, params = {}): Promise<Position[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        let userAddress: Str = undefined;
        [ userAddress, params ] = this.handleOriginAndSingleAddress ('fetchPositions', params);
        symbols = this.marketSymbols (symbols);
        const request: Dict = {
            'account': userAddress,
        };
        const response = await this.publicGetPositions (this.extend (request, params));
        // {
        //   "success": true,
        //   "data": [
        //     {
        //       "symbol": "AAVE",
        //       "side": "ask",
        //       "amount": "223.72",
        //       "entry_price": "279.283134",
        //       "margin": "0", // only shown for isolated margin
        //       "funding": "13.159593",
        //       "isolated": false,
        //       "created_at": 1754928414996,
        //       "updated_at": 1759223365538
        //     }
        //   ],
        //   "error": null,
        //   "code": null,
        //   "last_order_id": 1557431179
        // }
        const data = this.safeList (response, 'data', []);
        const result: Position[] = [];
        for (let i = 0; i < data.length; i++) {
            result.push (this.parsePosition (data[i], undefined));
        }
        return this.filterByArrayPositions (result, 'symbol', symbols, false);
    }

    override parsePosition (position: Dict, market: Market = undefined) {
        //
        //     {
        //       "symbol": "AAVE",
        //       "side": "ask",
        //       "amount": "223.72",
        //       "entry_price": "279.283134",
        //       "margin": "0", // only shown for isolated margin
        //       "funding": "13.159593",
        //       "isolated": false,
        //       "created_at": 1754928414996,
        //       "updated_at": 1759223365538
        //     }
        //
        const marketId = this.safeString (position, 'symbol');
        market = this.safeMarket (marketId, market);
        const symbol = market['symbol'];
        const margin = this.safeString (position, 'margin');
        const marginMode = (margin !== undefined && margin !== '0') ? 'isolated' : 'cross';
        const isIsolated = (marginMode === 'isolated');
        let side = this.safeString (position, 'side');
        if (side !== undefined) {
            side = (side === 'bid') ? 'long' : 'short';
        }
        const createdAt = this.safeInteger (position, 'created_at');
        return this.safePosition ({
            'info': position,
            'id': undefined,
            'symbol': symbol,
            'timestamp': createdAt,
            'datetime': this.iso8601 (createdAt),
            'isolated': isIsolated,
            'hedged': undefined,
            'side': side,
            'contracts': this.safeNumber (position, 'amount'),
            'contractSize': undefined,
            'entryPrice': this.safeNumber (position, 'entry_price'),
            'markPrice': undefined,
            'notional': undefined,
            'leverage': undefined,
            'collateral': margin,
            'initialMargin': undefined,
            'maintenanceMargin': undefined,
            'initialMarginPercentage': undefined,
            'maintenanceMarginPercentage': undefined,
            'unrealizedPnl': undefined,
            'liquidationPrice': undefined,
            'marginMode': marginMode,
            'percentage': undefined,
        });
    }

    /**
     * @method
     * @name pacifica#setMarginMode
     * @description set margin mode (symbol)
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/account/update-margin-mode
     * @param {string} marginMode margin mode must be either [isolated, cross]
     * @param {string} symbol unified market symbol of the market the position is held in, default is undefined
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.expiryWindow] time to live in milliseconds
     * @returns {object} response from the exchange
     */
    override async setMarginMode (marginMode: string, symbol: Str = undefined, params = {}) {
        const operationType = 'update_margin_mode';
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' setMarginMode() requires a symbol argument');
        }
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const isIsolated = (marginMode === 'isolated');
        const sigPayload: Dict = {
            'symbol': market['id'],
            'is_isolated': isIsolated,
        };
        const request = this.postActionRequest (operationType, sigPayload, params);
        params = this.omit (params, [ 'expiryWindow' ]);
        const response = await this.privatePostAccountMargin (request);
        // {
        //     "success": true
        // }
        return response;
    }

    /**
     * @method
     * @name pacifica#setLeverage
     * @description set the level of leverage for a market
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/account/update-leverage
     * @param {float} leverage the rate of leverage
     * @param {string} symbol unified market symbol
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.expiryWindow] time to live in milliseconds
     * @returns {object} response from the exchange
     */
    override async setLeverage (leverage: int, symbol: Str = undefined, params = {}) {
        const operationType = 'update_leverage';
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' setMarginMode() requires a symbol argument');
        }
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const sigPayload: Dict = {
            'symbol': market['id'],
            'leverage': leverage,
        };
        const request = this.postActionRequest (operationType, sigPayload, params);
        params = this.omit (params, [ 'expiryWindow' ]);
        const response = await this.privatePostAccountLeverage (request);
        // {
        //     "success": true
        // }
        return response;
    }

    /**
     * @method
     * @name pacifica#withdraw
     * @description make a withdrawal (only support native USDC)
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/account/request-withdrawal
     * @param {string} code unified currency code
     * @param {float} amount the amount to withdraw
     * @param {string} address the address to withdraw to
     * @param {string} tag
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.expiryWindow] time to live in milliseconds
     * @returns {object} a [transaction structure]{@link https://docs.ccxt.com/?id=transaction-structure}
     */
    override async withdraw (code: string, amount: number, address: string, tag: Str = undefined, params = {}): Promise<Transaction> {
        const operationType = 'withdraw';
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        this.checkAddress (address);
        const sigPayload: Dict = {
            'amount': amount.toString (),
        };
        const request = this.postActionRequest (operationType, sigPayload, params);
        params = this.omit (params, [ 'expiryWindow' ]);
        const response = await this.privatePostAccountWithdraw (this.extend (request, params));
        return { 'info': response } as Transaction;
    }

    /**
     * @method
     * @name pacifica#fetchTradingFee
     * @description fetch the trading fees for a market
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/account/get-account-info
     * @param {string} symbol unified market symbol
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @returns {object} a [fee structure]{@link https://docs.ccxt.com/?id=fee-structure}
     */
    override async fetchTradingFee (symbol: string, params = {}): Promise<TradingFeeInterface> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        let userAddress: Str = undefined;
        [ userAddress, params ] = this.handleOriginAndSingleAddress ('fetchTradingFee', params);
        const market = this.market (symbol);
        const request: Dict = {
            'account': userAddress,
        };
        const response = await this.publicGetAccount (this.extend (request, params));
        // {
        //   "success": true,
        //   "data": {
        //     "balance": "2000.000000",
        //     "fee_level": 0,
        //     "maker_fee": "0.00015",
        //     "taker_fee": "0.0004",
        //     "account_equity": "2150.250000",
        //     "available_to_spend": "1800.750000",
        //     "available_to_withdraw": "1500.850000",
        //     "pending_balance": "0.000000",
        //     "total_margin_used": "349.500000",
        //     "cross_mmr": "420.690000",
        //     "positions_count": 2,
        //     "orders_count": 3,
        //     "stop_orders_count": 1,
        //     "updated_at": 1716200000000,
        //     "use_ltp_for_stop_orders": false
        //   },
        //   "error": null,
        //   "code": null
        // }
        const data = this.safeDict (response, 'data', {});
        return this.parseTradingFee (data, market);
    }

    parseTradingFee (fee: Dict, market: Market = undefined): TradingFeeInterface {
        //
        //   {
        //     "balance": "2000.000000",
        //     "fee_level": 0,
        //     "maker_fee": "0.00015",
        //     "taker_fee": "0.0004",
        //     "account_equity": "2150.250000",
        //     "available_to_spend": "1800.750000",
        //     "available_to_withdraw": "1500.850000",
        //     "pending_balance": "0.000000",
        //     "total_margin_used": "349.500000",
        //     "cross_mmr": "420.690000",
        //     "positions_count": 2,
        //     "orders_count": 3,
        //     "stop_orders_count": 1,
        //     "updated_at": 1716200000000,
        //     "use_ltp_for_stop_orders": false
        //   }
        //
        //
        const symbol = this.safeSymbol (undefined, market);
        return {
            'info': fee,
            'symbol': symbol,
            'maker': this.safeNumber (fee, 'maker_fee'),
            'taker': this.safeNumber (fee, 'taker_fee'),
            'percentage': undefined,
            'tierBased': undefined,
        };
    }

    /**
     * @method
     * @name pacifica#fetchOpenInterests
     * @description Retrieves the open interest for a list of symbols
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/markets/get-prices
     * @param {string[]} [symbols] Unified CCXT market symbol
     * @param {object} [params] exchange specific parameters
     * @returns {object} an open interest structure{@link https://docs.ccxt.com/?id=open-interest-structure}
     */
    override async fetchOpenInterests (symbols: Strings = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        symbols = this.marketSymbols (symbols);
        const swapMarkets = await this.fetchSwapMarkets ();
        return this.parseOpenInterests (swapMarkets, symbols) as OpenInterests;
    }

    /**
     * @method
     * @name pacifica#fetchOpenInterest
     * @description retrieves the open interest of a contract trading pair
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/markets/get-prices
     * @param {string} symbol unified CCXT market symbol
     * @param {object} [params] exchange specific parameters
     * @returns {object} an [open interest structure]{@link https://docs.ccxt.com/?id=open-interest-structure}
     */
    override async fetchOpenInterest (symbol: string, params = {}) {
        symbol = this.symbol (symbol);
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const ois = await this.fetchOpenInterests ([ symbol ], params);
        return ois[symbol];
    }

    override parseOpenInterest (interest: any, market: Market = undefined) {
        //
        //     {
        //       "funding": "0.00010529",
        //       "mark": "1.084819",
        //       "mid": "1.08615",
        //       "next_funding": "0.00011096",
        //       "open_interest": "3634796",
        //       "oracle": "1.084524",
        //       "symbol": "XPL",
        //       "timestamp": 1759222967974,
        //       "volume_24h": "20896698.0672",
        //       "yesterday_price": "1.3412"
        //     }
        //
        const marketId = this.safeString (interest, 'symbol');
        let symbol: Str = undefined;
        if (marketId !== undefined) {
            market = this.safeMarket (marketId, market);
            symbol = market['symbol'];
        }
        let interestValue: Str = undefined;
        const markPrice = this.safeString (interest, 'mark');
        const openInterest = this.safeString (interest, 'open_interest');
        if ((openInterest !== undefined) && (markPrice !== undefined)) {
            interestValue = Precise.stringMul (openInterest, markPrice);
        }
        const timestamp = this.safeInteger (interest, 'timestamp');
        return this.safeOpenInterest ({
            'symbol': this.safeSymbol (symbol),
            'openInterestAmount': this.parseNumber (openInterest),
            'openInterestValue': this.parseNumber (interestValue),
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'info': interest,
        }, market);
    }

    /**
     * @method
     * @name pacifica#fetchLedger
     * @description fetch the history of changes, actions done by the user or operations that altered the balance of the user
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/account/get-account-balance-history
     * @param {string} [code] unified currency code
     * @param {int} [since] timestamp in ms of the earliest ledger entry
     * @param {int} [limit] max number of ledger entries to return
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @param {string} [params.cursor] pagination cursor from prev request (manual use)
     * @param {boolean} [params.paginate] default false, when true will automatically paginate by calling this endpoint multiple times. See in the docs all the [availble parameters](https://github.com/ccxt/ccxt/wiki/Manual#pagination-params)
     * @returns {object} a [ledger structure]{@link https://docs.ccxt.com/?id=ledger-entry-structure}
     */
    override async fetchLedger (code: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<LedgerEntry[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        let paginate = false;
        [ paginate, params ] = this.handleOptionAndParams (params, 'fetchLedger', 'paginate', false);
        let userAddress: Str = undefined;
        [ userAddress, params ] = this.handleOriginAndSingleAddress ('fetchLedger', params);
        const defaultLimit = 100; // Default max limit
        if (paginate) {
            return await this.fetchPaginatedCallCursor ('fetchLedger', code, since, limit, params, 'next_cursor', 'cursor', undefined, defaultLimit) as LedgerEntry[];
        }
        const request: Dict = {
            'account': userAddress,
        };
        if (limit !== undefined) {
            request['limit'] = limit;
        }
        const response = await this.publicGetAccountBalanceHistory (this.extend (request, params));
        // {
        //   "success": true,
        //   "data": [
        //     {
        //       "amount": "100.000000",
        //       "balance": "1200.000000",
        //       "pending_balance": "0.000000",
        //       "event_type": "deposit",
        //       "created_at": 1716200000000
        //     }
        //     ...
        //   ],
        //   "next_cursor": "11114Lz77",
        //   "has_more": true
        // }
        const data = this.addPaginationCursorToResult (response);
        return this.parseLedger (data, undefined, since, limit);
    }

    override parseLedgerEntry (item: Dict, currency: Currency = undefined): LedgerEntry {
        //
        //     {
        //       "amount": "100.000000",
        //       "balance": "1200.000000",
        //       "pending_balance": "0.000000",
        //       "event_type": "deposit",
        //       "created_at": 1716200000000
        //     }
        //
        const timestamp = this.safeInteger (item, 'created_at');
        const type = this.safeString (item, 'event_type');
        const amount = this.safeString (item, 'amount');
        const balance = this.safeString (item, 'balance');
        return this.safeLedgerEntry ({
            'info': item,
            'id': undefined,
            'direction': undefined,
            'account': undefined,
            'referenceAccount': undefined,
            'referenceId': undefined,
            'type': this.parseLedgerEntryType (type),
            'currency': undefined,
            'amount': this.parseNumber (amount),
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'before': undefined,
            'after': this.parseNumber (balance),
            'status': undefined,
            'fee': undefined,
        }, currency) as LedgerEntry;
    }

    parseLedgerEntryType (type: any) {
        const ledgerType: Dict = {
            'subaccount_transfer': 'transfer',
            'deposit': 'transaction',
            'deposit_release': 'transaction',
            'withdraw': 'transaction',
            'trade': 'trade',
            'market_liquidation': 'trade',
            'backstop_liquidation': 'trade',
            'adl_liquidation': 'trade',
            'funding': 'funding',
            'fee': 'fee',
            'rebate': 'rebate',
            'cashback': 'cashback',
            'referral': 'referral',
            'airdrop': 'airdrop',
            'payout': 'payout',
        };
        return this.safeString (ledgerType, (type as string), type);
    }

    /**
     * @method
     * @name pacifica#fetchFundingHistory
     * @description fetch the history of funding payments paid and received on this account
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/account/get-funding-history
     * @param {string} [symbol] unified market symbol
     * @param {int} [since] the earliest time in ms to fetch funding history for
     * @param {int} [limit] the maximum number of funding history structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.account] will default to walletAddress if not provided
     * @param {string} [params.cursor] pagination cursor from prev request
     * @param {boolean} [params.paginate] default false, when true will automatically paginate by calling this endpoint multiple times. See in the docs all the [availble parameters](https://github.com/ccxt/ccxt/wiki/Manual#pagination-params)
     * @returns {object} a [funding history structure]{@link https://docs.ccxt.com/?id=funding-history-structure}
     */
    override async fetchFundingHistory (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        let market: Market = undefined;
        if (symbol !== undefined) {
            market = this.market (symbol);
        }
        let paginate = false;
        [ paginate, params ] = this.handleOptionAndParams (params, 'fetchFundingHistory', 'paginate', false);
        let userAddress: Str = undefined;
        [ userAddress, params ] = this.handleOriginAndSingleAddress ('fetchFundingHistory', params);
        const request: Dict = {
            'account': userAddress,
        };
        if (limit !== undefined) {
            request['limit'] = limit;
        }
        const defaultLimit = 100;
        if (paginate) {
            return await this.fetchPaginatedCallCursor ('fetchFundingHistory', symbol, since, limit, params, 'next_cursor', 'cursor', undefined, defaultLimit) as FundingHistory[];
        }
        const response = await this.publicGetFundingHistory (this.extend (request, params));
        // {
        //   "success": true,
        //   "data": [
        //     {
        //       "history_id": 2287920,
        //       "symbol": "PUMP",
        //       "side": "ask",
        //       "amount": "39033804",
        //       "payout": "2.617479",
        //       "rate": "0.0000125",
        //       "created_at": 1759222804122
        //     },
        //     ...
        //   ],
        //   "next_cursor": "11114Lz77",
        //   "has_more": true
        // }
        const data = this.addPaginationCursorToResult (response);
        return this.parseIncomes (data, market, since, limit);
    }

    override parseIncome (income: any, market: Market = undefined) {
        //
        //     {
        //       "history_id": 2287920,
        //       "symbol": "PUMP",
        //       "side": "ask",
        //       "amount": "39033804",
        //       "payout": "2.617479",
        //       "rate": "0.0000125",
        //       "created_at": 1759222804122
        //     }
        //
        const id = this.safeString (income, 'history_id');
        const timestamp = this.safeInteger (income, 'created_at');
        const marketId = this.safeString (income, 'symbol');
        market = this.safeMarket (marketId, market);
        const symbol = market['symbol'];
        const amount = this.safeString (income, 'amount');
        const code = this.safeCurrencyCode ('USDC');
        const rate = this.safeNumber (income, 'rate');
        return {
            'info': income,
            'symbol': symbol,
            'code': code,
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'id': id,
            'amount': this.parseNumber (amount),
            'rate': rate,
        };
    }

    /**
     * @method
     * @name pacifica#transfer
     * @description transfer currency internally between wallets on the same account
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/subaccounts/subaccount-fund-transfer
     * @param {string} code unified currency code
     * @param {float} amount amount to transfer
     * @param {string} fromAccount account to transfer from *spot, swap*
     * @param {string} toAccount account to transfer to *swap, spot or address*
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.expiryWindow] time to live in milliseconds
     * @returns {object} a [transfer structure]{@link https://docs.ccxt.com/?id=transfer-structure}
     */
    override async transfer (code: string, amount: number, fromAccount: string, toAccount: string, params = {}): Promise<TransferEntry> {
        const operationType = 'transfer_funds';
        const sigPayload = {
            'to_account': toAccount,
            'amount': amount,
        };
        const request = this.postActionRequest (operationType, sigPayload, params);
        params = this.omit (params, [ 'expiryWindow' ]);
        const response = this.privatePostAccountSubaccountTransfer (this.extend (request, params));
        //
        // {
        //   "success": true,
        //   "data": {
        //     "success": true,
        //     "error": null
        //   },
        //   "error": null,
        //   "code": null
        // }
        //
        const data = this.safeDict (response, 'data', {});
        return this.parseTransfer (data);
    }

    override parseTransfer (transfer: Dict, currency: Currency = undefined): TransferEntry {
        //
        // {
        //   "success": true,
        //   "data": {
        //     "success": true,
        //     "error": null
        //   },
        //   "error": null,
        //   "code": null
        // }
        //
        return {
            'info': transfer,
            'id': undefined,
            'timestamp': undefined,
            'datetime': undefined,
            'currency': undefined,
            'amount': undefined,
            'fromAccount': undefined,
            'toAccount': undefined,
            'status': 'ok',
        };
    }

    /**
     * @method
     * @name pacifica#createSubAccount
     * @description creates a sub-account under the main account
     * @see https://docs.pacifica.fi/api-documentation/api/rest-api/subaccounts/create-subaccount
     * @param {string} name unused argument
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.expiryWindow] time to live in milliseconds
     * @param {string} [params.subAccountAddress] - The public key (address) of the sub-account to use for creation
     * @param {string} [params.subAccountPrivateKey] - The private key of the sub-account to use for creation
     * @returns {object} a response object
     */
    override async createSubAccount (name: string, params = {}) {
        const finalHeaders: Dict = { };
        let agentAddress: Str = undefined;
        [ agentAddress, params ] = this.handleOption ('createSubAccount', 'agentAddress');
        let originAddress: Str = undefined;
        [ originAddress, params ] = this.handleOriginAndSingleAddress ('createSubAccount', params);
        if (originAddress === undefined) {
            throw new ArgumentsRequired (this.id + ' createSubAccount() requires "originAddress" in params or "walletAddress" in requiredCredentials');
        }
        if (agentAddress !== undefined) {
            finalHeaders['agent_wallet'] = agentAddress;
        }
        let subAccountAddress: Str = undefined;
        [ subAccountAddress, params ] = this.handleOptionAndParams (params, 'createSubAccount', 'subAccountAddress');
        let subAccountPrivateKey: Str = undefined;
        [ subAccountPrivateKey, params ] = this.handleOptionAndParams (params, 'createSubAccount', 'subAccountPrivateKey');
        if (subAccountAddress === undefined) {
            throw new ArgumentsRequired (this.id + ' createSubAccount() requires a "subAccountAddress"!');
        }
        if (subAccountPrivateKey === undefined) {
            throw new ArgumentsRequired (this.id + ' createSubAccount() requires a "subAccountPrivateKey"!');
        }
        const timestamp = this.milliseconds ();
        let expiryWindow: Int = undefined;
        [ expiryWindow, params ] = this.handleOptionAndParams2 (params, 'createSubAccount', 'expiryWindow', 'expiry_window', 5000);
        const subaccountSignatureHeader = {
            'timestamp': timestamp,
            'expiry_window': expiryWindow,
            'type': 'subaccount_initiate',
        };
        const subSigPayload = {
            'account': originAddress,
        };
        const subaccountSignature = this.signMessage (subaccountSignatureHeader, subSigPayload, subAccountPrivateKey);
        const mainSignatureHeader = {
            'timestamp': timestamp,
            'expiry_window': expiryWindow,
            'type': 'subaccount_confirm',
        };
        const mainSigPayload = {
            'signature': subaccountSignature,
        };
        const main_signature = this.signMessage (mainSignatureHeader, mainSigPayload, this.privateKey);
        finalHeaders['main_account'] = originAddress;
        finalHeaders['subaccount'] = subAccountAddress;
        finalHeaders['sub_signature'] = subaccountSignature;
        finalHeaders['main_signature'] = main_signature;
        finalHeaders['timestamp'] = timestamp;
        finalHeaders['expiry_window'] = expiryWindow;
        const request = finalHeaders;
        const response = await this.privatePostAccountSubaccountCreate (request);
        //
        // {
        //   "success": true,
        //   "data": null,
        //   "error": null,
        //   "code": null,
        // }
        //
        return response;
    }

    async bindAgentWallet (agentAddress: string, params = {}) {
        const operationType = 'bind_agent_wallet';
        const sigPayload = {
            'agent_wallet': agentAddress,
        };
        const request = this.postActionRequest (operationType, sigPayload, params);
        return await this.privatePostAgentBind (this.extend (request, params));
    }

    async createApiKey (params = {}) {
        const operationType = 'create_api_key';
        const sigPayload = {};
        const request = this.postActionRequest (operationType, sigPayload, params);
        return await this.privatePostAccountApiKeysCreate (this.extend (request, params));
    }

    async revokeApiKey (apiKey: string, params = {}) {
        const operationType = 'revoke_api_key';
        const sigPayload = {
            'api_key': apiKey,
        };
        const request = this.postActionRequest (operationType, sigPayload, params);
        return await this.privatePostAccountApiKeysRevoke (this.extend (request, params));
    }

    async fetchApiKeys (params = {}) {
        const operationType = 'list_api_keys';
        const sigPayload = {};
        const request = this.postActionRequest (operationType, sigPayload, params);
        return await this.privatePostAccountApiKeys (this.extend (request, params));
    }

    async approveBuilderCode (builderCode: string, maxFeeRate: string, params = {}) {
        const operationType = 'approve_builder_code';
        const sigPayload = {
            'builder_code': builderCode,
            'max_fee_rate': maxFeeRate,
        };
        const request = this.postActionRequest (operationType, sigPayload, params);
        return await this.privatePostAccountBuilderCodesApprove (this.extend (request, params));
    }

    async fetchBuilderApprovals (address: string) {
        const request = {
            'account': address,
        };
        return await this.publicGetAccountBuilderCodesApprovals (this.extend (request));
    }

    async revokeBuilderCode (builderCode: string, params = {}) {
        const operationType = 'revoke_builder_code';
        const sigPayload = {
            'builder_code': builderCode,
        };
        const request = this.postActionRequest (operationType, sigPayload, params);
        return await this.privatePostAccountBuilderCodesRevoke (this.extend (request, params));
    }

    handleOriginAndSingleAddress (methodName: string, params: Dict): [Str, Dict] {
        let address: Str = undefined;
        [ address, params ] = this.handleParamString2 (params, 'account', 'address', undefined); // this is for get endpoints that accept account or address
        if (address !== undefined) {
            return [ address, params ];
        }
        const address1 = this.walletAddress;
        if (address1 !== undefined) {
            return [ address1, params ];
        }
        throw new ArgumentsRequired (this.id + ' ' + methodName + '() requires address either as "exchange.walletAddress = ..." or as parameter or "address" in params');
    }

    override handleErrors (code: int, reason: string, url: string, method: string, headers: Dict, body: string, response: any, requestHeaders: any, requestBody: any) {
        if (response === undefined) {
            return undefined; // fallback to default error handler
        }
        //
        //     {"success":false,"data":null,"error":"Beta access required. Signer must redeem a valid beta code.","code":403}
        //     {"success":false,"data":null,"error":"Agent not authorized for account","code":400}
        //     {"success":false,"data":null,"error":"Internal server error","code":500}
        //
        const inCode = this.safeInteger (response, 'code'); // actually if all ok -> code = undefined or code = 200
        const message = this.safeString (response, 'error');
        let error: Bool = undefined;
        if (inCode === undefined || inCode === 200) {
            error = false;
        } else {
            error = true;
        }
        const nonEmptyMessage = ((message !== undefined) && (message !== ''));
        if (error || nonEmptyMessage) {
            const feedback = this.id + ' ' + body;
            this.throwBroadlyMatchedException (this.exceptions['broad'], message, feedback); // Try deeper catch first
            this.throwExactlyMatchedException (this.exceptions['exact'], inCode, feedback);
            this.throwExactlyMatchedException (this.exceptions['exact'], message, feedback);
            throw new ExchangeError (feedback); // unknown message
        }
        return undefined;
    }

    override sign (path: any, api: any = 'public', method = 'GET', params = {}, headers: NullableDict = undefined, body: Str = undefined) {
        const isTestnet = this.isSandboxModeEnabled;
        const urlKey = (isTestnet) ? 'test' : 'api';
        const host = this.implodeHostname (this.urls[urlKey][api]);
        let url = host + '/api/' + this.version + '/' + this.implodeParams (path, params);
        params = this.omit (params, this.extractParams (path));
        const paramsLen = Object.keys (params).length;
        headers = {
            'Content-Type': 'application/json',
        };
        if (method === 'GET' && paramsLen) {
            url += '?' + this.urlencode (params);
            headers['Accept'] = '*/*';
        }
        if (method === 'POST') {
            body = this.json (params);
        }
        if (this.handleOption ('sign', 'apiKey') !== undefined) {
            headers['PF-API-KEY'] = this.options['apiKey'];
        }
        return { 'url': url, 'method': method, 'body': body, 'headers': headers };
    }

    override calculateRateLimiterCost (api: any, method: any, path: any, params: any, config = {}) {
        const cost = this.safeString (config, 'cost', '1');
        const costNumber = this.parseNumber (cost);
        // 1 is normal POST/GET, 0.5 is cancels, 3-12 is heavy GET
        if (costNumber > 1) {
            if (this.handleOption (method, 'apiKey') !== undefined) {
                const costWithKey = this.handleOption (
                    method,
                    'maxCostHugeWithApiKey',
                    3
                );
                return costWithKey;
            }
        }
        return costNumber;
    }

    sortJsonKeys (value: any): any {
        if (this.isDictionary (value)) {
            const result: Dict = {};
            const keys = Object.keys (value);
            const sortedKeys = this.sort (keys);
            for (let i = 0; i < sortedKeys.length; i++) {
                const key = sortedKeys[i];
                result[key] = this.sortJsonKeys (value[key]);
            }
            return result;
        } else if (Array.isArray (value)) {
            const result: Dict[] = [];
            for (let i = 0; i < value.length; i++) {
                result.push (this.sortJsonKeys (value[i]));
            }
            return result;
        } else {
            return value;
        }
    }

    prepareMessage (header: Dict, payload: Dict): string {
        if (header['type'] === undefined || header['timestamp'] === undefined || header['expiry_window'] === undefined) {
            throw new ArgumentsRequired (this.id + ' prepareMessage() requires type, timestamp, expiry_window in header');
        }
        const data = this.extend (header, { 'data': payload });
        const sorted = this.sortJsonKeys (data);
        return this.json (sorted);
    }

    signMessage (header: Dict, payload: Dict, privateKey: string): string {
        const message = this.prepareMessage (header, payload);
        const messageBytes = this.encode (message);
        const secretBytes = this.base58ToBinary (privateKey);
        const seed = this.arraySlice (secretBytes, 0, 32);
        const signatureBase64 = eddsa (messageBytes, seed, ed25519);
        const signatureBinary = this.base64ToBinary (signatureBase64);
        const signatureBase58 = this.binaryToBase58 (signatureBinary);
        return signatureBase58;
    }

    postActionRequest (operationType: Str, sigPayload: Dict, params: Dict): Dict {
        this.checkRequiredCredentials (); // check credentials every post action
        if (operationType === 'undefined') {
            throw new ArgumentsRequired (this.id + ' action: ' + operationType + ' postActionRequest() requires "operationType"');
        }
        if (!this.isSandboxModeEnabled) { // At this stage, building codes are mostly only on the mainnet.
            const useBuilder = this.handleOption ('postActionRequest', 'builderFee', true);
            let builderCode: Str = undefined;
            if (useBuilder) {
                builderCode = this.handleOption ('postActionRequest', 'builderCode');
            }
            if (builderCode !== undefined) {
                const isOperationSupportBuilder = this.safeBool (this.options['builderSupportOperations'], operationType, false);
                if (isOperationSupportBuilder) {
                    sigPayload['builder_code'] = builderCode;
                }
            }
        }
        let expiryWindow: Int = undefined;
        [ expiryWindow, params ] = this.handleOptionAndParams2 (params, 'postActionRequest', 'expiryWindow', 'expiry_window', 5000);
        const timestamp = this.safeInteger (params, 'timestamp', this.milliseconds ());
        const signatureHeader = {
            'timestamp': timestamp,
            'expiry_window': expiryWindow,
            'type': operationType,
        };
        const signature = this.signMessage (signatureHeader, sigPayload, this.privateKey);
        const finalHeaders: Dict = { };
        let agentAddress: Str = undefined;
        [ agentAddress, params ] = this.handleOptionAndParams (params, 'postActionRequest', 'agentAddress');
        let originAddress: Str = undefined;
        [ originAddress, params ] = this.handleOriginAndSingleAddress ('postActionRequest', params);
        if (originAddress === undefined) {
            throw new ArgumentsRequired (this.id + ' action: ' + operationType + ' postActionRequest() requires "originAddress" in params or "walletAddress" in requiredCredentials');
        }
        finalHeaders['account'] = originAddress;
        if (agentAddress !== undefined) {
            finalHeaders['agent_wallet'] = agentAddress;
        }
        finalHeaders['signature'] = signature;
        finalHeaders['timestamp'] = this.safeInteger (signatureHeader, 'timestamp');
        finalHeaders['expiry_window'] = this.safeInteger (signatureHeader, 'expiry_window');
        const request = this.extend (finalHeaders, sigPayload);
        return request;
    }
}
